/* The design's copy, transcribed.
 *
 * Every string below is read off docs/design/v3/*.html -- the AR from the
 * -ar- files, the EN from the -en- ones. It is not translated here and not
 * rewritten here: where the design says «بيتك الجاي، لقيه هنا», so does this
 * file. Copy that the design does not carry is marked at its definition.
 *
 * ONE DELIBERATE CHANGE, ruled by Bassem: digits stay Western. The design
 * writes «٢٠٢٦» and «٩٫٢ مليون»; src/lib/format.ts requests the latn
 * numbering system for Arabic, and that stands. Logged in docs/DEVIATIONS.md.
 *
 * COPY THAT LIVES IN THE DATABASE is not here. The hero title and subtitle,
 * the lister band and the nav labels are `site_settings` rows; these are the
 * fallbacks used before the settings arrive, and they are the design's words
 * so the fallback and the seed agree.
 */

import type { Locale } from "@/i18n/strings";

const ar = {
  header: {
    main: "Main",
    menu: "القائمة",
    login: "تسجيل الدخول",
    listProperty: "اعرض عقارك",
    otherLocale: "EN",
    otherLocaleLang: "en",
  },
  nav: {
    buy: "شراء",
    rent: "إيجار",
    projects: "مشاريع جديدة",
    advisor: "المستشار الذكي",
    plans: "الباقات",
  },
  marketComingSoon: {
    /* Beside a market's name wherever it is offered. */
    tag: "قريبًا",
    title: "{market} — قريبًا.",
    body: "لم نفتح هذا السوق بعد، فلا توجد عقارات هنا حتى الآن.",
    go: "تصفّح {market}",
  },
  marketSuggestion: {
    /* "{market}" is the suggested market's own name, from its row. */
    lead: "يبدو أنك في {market}.",
    leadLong: "يبدو أنك في {market}. تحب تتصفح سوق {market}؟",
    go: "انتقل إلى /{slug}/",
    goShort: "تصفح سوق {market}",
    close: "إغلاق",
  },
  hero: {
    title: "بيتك الجاي، لقيه هنا",
    subtitle: "ابحث في شقق وفلل وشاليهات ومشاريع جديدة، وتواصل مباشرة.",
    tabBuy: "شراء",
    tabRent: "إيجار",
    tabProjects: "مشاريع",
    location: "الموقع",
    locationPlaceholder: "المدينة أو المنطقة",
    /* Not design copy: the design's location cell is a plain input, and this
       is what it says when what was typed matches no city or area. The rule
       it serves is the no-silent-disabled law -- the field explains itself on
       attempt rather than searching for nothing. */
    locationUnknown: "مفيش مدينة أو منطقة بالاسم ده. اختر واحدة من القائمة.",
    priceMin: "من",
    priceMax: "إلى",
    type: "النوع",
    typeAny: "كل الأنواع",
    price: "السعر",
    priceAny: "أي سعر",
    rooms: "الغرف",
    roomsAny: "أي عدد",
    search: "ابحث",
  },
  sections: {
    browseTypes: "تصفح حسب النوع",
    /* The last tile when more types exist than the home row shows. */
    /* The last tile when more areas exist than the home row shows. */
    allAreas: "كل المناطق ←",
    allTypes: "كل الأنواع ←",
    browseAreas: "تصفح حسب المنطقة",
    featured: "عقارات مميزة",
    allListings: "كل العقارات ←",
    allListingsShort: "الكل",
    plans: "باقات المعلنين",
    plansHint: "الأسماء والأسعار والحدود تأتي من قاعدة البيانات",
    plansHintShort: "البيانات من قاعدة البيانات",
  },
  types: {
    Apartment: "شقة",
    Villa: "فيلا",
    Duplex: "دوبلكس",
    Studio: "استوديو",
    Chalet: "شاليه",
  } as Record<string, string>,
  listing: {
    forSale: "للبيع",
    forRent: "للإيجار",
    video: "فيديو",
    save: "حفظ",
    unsave: "إزالة من المحفوظات",
    beds: "غرف",
    baths: "حمام",
    sqm: "م²",
    /* Compact price words. The design writes «٩٫٢ مليون ج.م» and
       «٣٥ ألف ج.م / شهر»; the unit words are copy, the digits are not. */
    million: "مليون",
    thousand: "ألف",
    perMonth: "/ شهر",
  },
  featuredStates: {
    loading: "جارٍ تحميل العقارات المميزة",
    emptyTitle: "لا توجد عقارات مميزة حاليًا",
    emptyBody: "ابحث حسب المنطقة أو النوع، أو كن أول من يعرض عقاره.",
    emptyCta: "اعرض عقارك",
    error: "تعذّر تحميل العقارات المميزة.",
    retry: "إعادة المحاولة",
  },
  advisor: {
    badge: "المستشار الذكي",
    title: "اسأل قبل ما تقرر",
    body: "المستشار الذكي يقرأ بيانات الإعلان ويقارنه بالمنطقة، ويجاوبك: السعر عادل ولا لا؟ أشتري ولا أأجر؟ إيه المتوقع للمنطقة؟",
    /* The 390 file writes a shorter body, two bubbles and no composer. Not an
       abbreviation we invented -- it is the design's own mobile copy. */
    bodyShort: "السعر عادل ولا لا؟ أشتري ولا أأجر؟ المستشار يقرأ الإعلان ويقارنه بالمنطقة.",
    cta: "جرّب المستشار",
    sampleLabel: "مثال توضيحي",
    q1: "السعر ده مناسب لشقة ١٨٥ م² في التجمع الخامس؟",
    a1: "مقارنةً بالإعلانات المشابهة في المنطقة، السعر في النطاق المعتاد. لو الشقة استلام فوري ومتشطبة، فهو منطقي.",
    q2: "وأشتري ولا أأجر لو هقعد ٣ سنين بس؟",
    composerPlaceholder: "اسأل عن أي إعلان…",
    send: "إرسال",
  },
  listerBand: {
    title: "عندك عقار للبيع أو الإيجار؟",
    body: "انشر إعلانك في دقائق، واستقبل الرسائل من المهتمين مباشرة، من غير وسيط.",
    bodyShort: "انشر إعلانك في دقائق واستقبل رسائل المهتمين مباشرة.",
    cta: "اعرض عقارك",
  },
  plan: {
    listings: "إعلانات:",
    featured: "إعلانات مميزة:",
    aiCredits: "رصيد المستشار:",
    choose: "اختر الباقة",
    error: "تعذّر تحميل الباقات.",
    unlimited: "بلا حد",
    free: "مجانًا",
    perMonth: "/ شهر",
  },
  footer: {
    label: "Footer",
    markets: "الأسواق:",
    /* «© ٢٠٢٦ سوق عقار» in the design; the year is the build's, not a literal,
       and the digits are Western by ruling. */
    copyright: "© {year} سوق عقار",
    about: "من نحن",
    contact: "اتصل بنا",
    faq: "الأسئلة الشائعة",
    terms: "الشروط",
    privacy: "الخصوصية",
  },
  marketSwitcher: {
    label: "اختر السوق",
  },
  detail: {
    photos: "الصور",
    photoCount: "{n} صورة",
    videoTour: "جولة فيديو",
    save: "حفظ",
    saved: "محفوظ",
    share: "مشاركة",
    shared: "تم نسخ الرابط",
    publishedRef: "رقم الإعلان",
    type: "النوع",
    area: "المساحة",
    sqm: "م²",
    beds: "الغرف",
    baths: "الحمامات",
    kitchens: "المطابخ",
    furnished: "مفروش",
    yes: "نعم",
    no: "لا",
    licence: "رقم الترخيص الإعلاني",
    description: "الوصف",
    location: "الموقع",
    /* The design's own note under the map. It is a promise about what we
       show, so it is only rendered when it is true -- see the page. */
    locationApprox: "يُعرض الموقع التقريبي فقط حتى يتواصل المهتم مع المعلن.",
    play: "تشغيل الفيديو",
    contact: "تواصل مع المعلن",
    contactHint: "التواصل مباشر داخل الموقع، بدون وسيط أو رسوم.",
    memberSince: "عضو منذ",
    advisorTitle: "هل السعر مناسب لهذه المنطقة؟",
    advisorBody: "اسأل عن هذا الإعلان: السعر مقابل المنطقة، الشراء أو الإيجار، والمتوقع للمنطقة.",
    advisorCta: "اسأل المستشار",
    similar: "عقارات مشابهة",
    more: "المزيد ←",
    goneTitle: "هذا الإعلان لم يعد متاحًا",
    goneBody: "ربما تم بيعه أو أزاله المعلن. تصفح عقارات مشابهة في نفس المنطقة.",
    goneCta: "عقارات مشابهة",
    error: "تعذّر تحميل الإعلان. حاول مرة أخرى.",
    priceOnRequest: "السعر عند الطلب",
  },
  post: {
    steps: ["النوع", "الموقع", "التفاصيل", "الصور", "السعر", "المراجعة"],
    stepsLabel: "خطوات النشر",
    stepOf: "الخطوة {n} من {total}",
    saveDraft: "حفظ كمسودة",
    draftSaved: "تم حفظ المسودة على هذا الجهاز",
    autosaved: "آخر حفظ تلقائي",
    now: "الآن",
    prev: "السابق",
    next: "التالي",
    publish: "نشر الإعلان",
    /* step 1 */
    purpose: "الغرض",
    forSale: "للبيع",
    forRent: "للإيجار",
    t1: "نوع العقار والغرض",
    t1hint: "اختر الغرض ثم نوع العقار.",
    /* step 2 */
    t2: "موقع العقار",
    t2hint: "المدينة والمنطقة تظهران في الإعلان. الموقع الدقيق اختياري.",
    city: "المدينة",
    area: "المنطقة",
    pickCity: "اختر المدينة",
    pickArea: "اختر المنطقة",
    preciseLabel: "أعرض موقعًا دقيقًا على الخريطة",
    preciseHint: "لو تركته، يُعرض موقع تقريبي فقط.",
    /* step 3 */
    t3: "تفاصيل العقار",
    t3hint: "كل الحقول التالية تظهر في صفحة الإعلان.",
    sqm: "المساحة (م²)",
    landArea: "مساحة الأرض (م²)",
    beds: "غرف النوم",
    baths: "الحمامات",
    kitchens: "المطابخ",
    furnished: "مفروش",
    yes: "نعم",
    no: "لا",
    title: "عنوان الإعلان",
    titlePlaceholder: "مثال: شقة ٣ غرف بحديقة خاصة",
    titleHint: "قصير وواضح. من غير أرقام هاتف أو كلمات ترويجية.",
    description: "الوصف",
    descriptionPlaceholder: "اوصف العقار: الحالة، الإطلالة، المميزات، القرب من الخدمات…",
    licence: "رقم الترخيص الإعلاني",
    licencePlaceholder: "مطلوب في هذا السوق",
    /* step 4 */
    t4: "الصور والفيديو",
    t4hint: "أول صورة هي صورة الغلاف. رتّبها بالسحب.",
    video: "رابط جولة فيديو (اختياري)",
    /* step 5 */
    t5: "السعر",
    t5hint: "السعر يظهر في نتائج البحث وفي صفحة الإعلان.",
    price: "السعر",
    advisorPriceHint: "المستشار الذكي يقدر يقارن سعرك بالإعلانات المشابهة في نفس المنطقة بعد النشر.",
    /* step 6 */
    t6: "المراجعة",
    t6hint: "راجع البيانات قبل النشر. تقدر تعدل أي خطوة.",
    edit: "تعديل",
    sum1: "النوع والغرض",
    sum2: "الموقع",
    sum3: "التفاصيل",
    sum4: "الصور والفيديو",
    sum5: "السعر",
    /* The rule on the photos step. «{n}» is the market's minimum. */
    minPhotos: "لا يمكن نشر عقار بدون صور. أضف {n} صورة على الأقل.",
    minPhotosShort: "أضف {n} صورة على الأقل",
    photosN: "{n} صور",
    hasVideo: "فيديو",
    terms: "أوافق على شروط النشر: البيانات صحيحة، والصور للعقار المعلن عنه.",
    termsLink: "الشروط",
    /* outcomes */
    missing: "يرجى استكمال الحقول المطلوبة ({n}).",
    doneTitle: "تم إرسال إعلانك للمراجعة",
    doneBody: "عادةً تستغرق المراجعة وقتًا قصيرًا، وسنخبرك عند النشر.",
    dashboard: "لوحة التحكم",
    another: "إعلان آخر",
    limitTitle: "وصلت إلى حد الإعلانات في باقتك",
    limitBody: "باقتك تسمح بعدد محدود من الإعلانات النشطة. يمكنك حفظ هذا الإعلان كمسودة أو الترقية للنشر الآن.",
    upgrade: "ترقية الباقة",
    failed: "تعذّر نشر الإعلان. حاول مرة أخرى.",
    signInTitle: "سجّل الدخول لنشر إعلانك",
    signInBody: "الإعلان مرتبط بحسابك، فيه رسائل المهتمين وإحصاءات المشاهدة.",
    signIn: "تسجيل الدخول",
  },
  plans: {
    title: "باقات المعلنين",
    hint: "اختر ما يناسب عدد عقاراتك. الأسماء والأسعار والحدود تُدار من لوحة الإدارة لكل سوق.",
    perMonth: "/ شهر",
    activeListings: "إعلانات نشطة",
    aiCredits: "رصيد المستشار الذكي",
    current: "باقتك الحالية",
    currentCta: "الباقة الحالية",
    startFree: "ابدأ مجانًا",
    upgrade: "ترقية",
    choose: "اختر الباقة",
    compare: "مقارنة الباقات",
    unlimited: "بلا حد",
    free: "مجانًا",
    error: "تعذّر تحميل الباقات.",
    retry: "إعادة المحاولة",
    empty: "لا توجد باقات متاحة حاليًا.",
    faq: "أسئلة شائعة",
  },
  account: {
    nav: "حسابي",
    dashboard: "لوحة التحكم",
    listings: "إعلاناتي",
    messages: "الرسائل",
    plan: "باقتي",
    saved: "المحفوظات",
    profile: "حسابي",
    welcome: "أهلًا، {name}",
    welcomeHint: "هنا تتابع إعلاناتك ورسائلك وباقتك.",
    statActive: "إعلانات نشطة",
    statUnread: "رسائل جديدة",
    statThreads: "المحادثات",
    statViews: "إجمالي المشاهدات",
    statAi: "رصيد المستشار",
    newListing: "إعلان جديد",
    filterAll: "الكل",
    filterLive: "منشور",
    filterReview: "قيد المراجعة",
    filterPaused: "متوقف",
    filterRejected: "مرفوض",
    colListing: "الإعلان",
    colPrice: "السعر",
    colStatus: "الحالة",
    colViews: "مشاهدات",
    colMessages: "رسائل",
    statusLive: "منشور",
    statusReview: "قيد المراجعة",
    statusPaused: "متوقف",
    statusRejected: "مرفوض",
    edit: "تعديل",
    pause: "إيقاف مؤقت",
    resume: "إعادة النشر",
    view: "عرض",
    recentMessages: "آخر الرسائل",
    allMessages: "كل الرسائل ←",
    noMessages: "لا توجد رسائل بعد.",
    myPlan: "باقتي",
    usageListings: "الإعلانات النشطة",
    usageAi: "رصيد المستشار الذكي",
    unlimited: "بلا حد",
    emptyTitle: "لسه ما عندكش إعلانات",
    emptyBody: "انشر أول إعلان في دقائق، واستقبل رسائل المهتمين مباشرة.",
    error: "تعذّر تحميل بيانات لوحة التحكم.",
    localDraft: "عندك مسودة محفوظة على هذا الجهاز",
    continueDraft: "أكمل المسودة",
    unreadNote: "«رسائل جديدة» تُحسب على هذا الجهاز من آخر مرة فتحت فيها الرسائل.",
  },
  chat: {
    title: "الرسائل",
    all: "الكل",
    search: "ابحث في المحادثات…",
    conversation: "محادثة",
    deletedUser: "مستخدم محذوف",
    viewListing: "عرض الإعلان",
    publicProfile: "الملف العام",
    placeholder: "اكتب رسالتك…",
    message: "الرسالة",
    send: "إرسال",
    hint: "Enter للإرسال · Shift+Enter لسطر جديد",
    failed: "تعذّر إرسال الرسالة. حاول مرة أخرى.",
    empty: "لا توجد محادثات بعد",
    emptyBody: "لما تتواصل مع معلن، المحادثة هتظهر هنا.",
    error: "تعذّر تحميل المحادثات.",
    open: "المحادثات",
    close: "إغلاق",
    back: "رجوع",
    today: "اليوم",
    yesterday: "أمس",
    noneSelected: "اختر محادثة من القائمة.",
    signIn: "سجّل الدخول لقراءة رسائلك.",
  },
  profile: {
    memberSince: "عضو منذ",
    viewPublic: "عرض ملفي العام ←",
    personal: "البيانات الشخصية",
    name: "الاسم",
    phone: "رقم الهاتف",
    email: "البريد الإلكتروني",
    language: "اللغة",
    verified: "موثّق",
    notVerified: "غير موثّق",
    edit: "تعديل",
    save: "حفظ",
    cancel: "إلغاء",
    saveFailed: "تعذّر الحفظ. حاول مرة أخرى.",
    emailFixed: "البريد الإلكتروني هو معرّف الحساب ولا يُغيَّر من هنا.",
    security: "الأمان",
    password: "كلمة المرور",
    changePassword: "تغيير كلمة المرور",
    plan: "باقتي",
    upgrade: "ترقية الباقة",
    usageListings: "الإعلانات النشطة",
    usageAi: "رصيد المستشار الذكي",
    renewsAt: "تتجدد الباقة في",
    saved: "العقارات المحفوظة",
    unsave: "إزالة من المحفوظات",
    noSaved: "لسه ما حفظتش أي عقار.",
    signOut: "تسجيل الخروج",
    dangerTitle: "حذف الحساب",
    dangerBody: "سيتم إيقاف حسابك فورًا وإخفاء إعلاناتك. تُحذف بياناتك الشخصية نهائيًا بعد ٣٠ يومًا. تقدر تتراجع في أي وقت خلال المدة دي بتسجيل الدخول.",
    dangerKeeps: "الرسائل تبقى عند الطرف الآخر، ويظهر اسمك فيها «مستخدم محذوف».",
    dangerCta: "حذف الحساب",
    dangerConfirmTitle: "تأكيد حذف الحساب",
    dangerConfirmBody: "اكتب «حذف» للتأكيد.",
    dangerConfirmWord: "حذف",
    dangerConfirmCta: "تأكيد الحذف",
    dangerFailed: "تعذّر تنفيذ الطلب. حاول مرة أخرى.",
    pendingTitle: "حسابك مجدول للحذف",
    pendingBody: "تُحذف بياناتك نهائيًا بعد ٣٠ يومًا من {when}. سجّل الدخول قبل ذلك للتراجع.",
    pendingUnpublished: "أُخفيت {n} من إعلاناتك.",

    /* public profile */
    listerListings: "إعلانات {name}",
    contact: "تواصل",
    all: "الكل",
    forSale: "للبيع",
    forRent: "للإيجار",
    noListings: "لا توجد إعلانات منشورة حاليًا.",
    notFound: "هذا الملف غير متاح.",
  },
  cms: {
    /* Picture upload — hero and area tiles. */
    imageChoose: "اختر صورة",
    imageReplace: "استبدال الصورة",
    imageRemove: "إزالة",
    imagePreparing: "جارٍ تجهيز المقاسات…",
    imageUploading: "جارٍ الرفع… {done}/{total}",
    imageSizes: "المقاسات المرفوعة: {widths} بكسل — المتصفح يختار الأنسب للشاشة.",
    imageTooLarge: "الصورة أكبر من {mb} ميجابايت. اختر صورة أصغر.",
    imageWrongType: "الصيغ المقبولة: JPEG أو PNG أو WebP.",
    imageUnreadable: "تعذّر قراءة هذه الصورة.",
    imageUploadFailed: "تعذّر رفع الصورة. {detail}",
    areaImages: "صور المناطق المميزة",
    areaImagesHint: "صورة لكل منطقة. بدون صورة تبقى البلاطة بخلفية الموقع الكحلية كما في التصميم.",
    areaImagesNeedIds: "المناطق المشتقة من نص كتبه المعلن بلا معرّف ثابت لا يمكن ربط صورة بها.",
    /* Property types — Admin owns the list the whole site reads. */
    types: "أنواع العقارات",
    typesHint: "رتّب الأنواع بالسحب. هذا الترتيب هو ترتيب الموقع كله: بلاطات الصفحة الرئيسية، فلاتر البحث، وشاشة «اعرض عقارك».",
    typeAdd: "إضافة نوع",
    areasOrder: "ترتيب المناطق وظهورها",
    areasOrderHint: "رتّب المناطق بالسحب. هذا ترتيب الصفحة الرئيسية. إخفاء منطقة يمنع ظهورها في الرئيسية فقط — صفحة نتائجها تظل تعمل.",
    areaVisible: "ظاهرة في الرئيسية",
    areasPhotosFirst: "المناطق اللي ليها صور الأول",
    areasPhotosFirstHint: "مفعّل: المناطق اللي ليها صورة تظهر أولًا بترتيبك، بعدها اللي من غير صورة بترتيبك.",
    areasPhotosOnly: "اعرض المناطق اللي ليها صور بس",
    areasRequireListings: "اعرض المناطق اللي فيها إعلانات بس",
    areasRequireListingsHint: "لو مقفول (الوضع الافتراضي)، أي منطقة ليها صورة هتظهر في الرئيسية حتى لو لسه مفيش فيها إعلانات — وصفحتها هتوضّح ده بأمانة وتدعو صاحب العقار إنه يكون أول واحد يعرض فيها.",
    areasCount: "عدد المناطق في الرئيسية",
    areasCountHint: "لو المناطق الظاهرة أكتر من الرقم ده، تبقى البلاطة الأخيرة «كل المناطق ←».",
    imageTooNarrow: "الصورة صغيرة وقد تظهر غير واضحة على الشاشات الكبيرة — يُفضّل 1600 بكسل أو أكثر.",
    minPhotosSetting: "أقل عدد صور للنشر",
    minPhotosSettingHint: "لا يُنشر أي عقار في هذا السوق بأقل من هذا العدد من الصور. الحد الأدنى 1. يسري على «اعرض عقارك» وعلى موافقة الإدارة. الإعلانات الحالية لا تتغيّر.",
    homeTypeCount: "عدد الأنواع في الرئيسية",
    homeTypeCountHint: "عدد البلاطات في «تصفّح حسب النوع». لو كان عدد الأنواع أكبر، تصبح البلاطة الأخيرة «كل الأنواع ←». إخفاء نوع من شاشة الأنواع يزيله من الموقع كله؛ هذا الرقم يحدّ صف الرئيسية فقط.",
    typeEdit: "تعديل",
    typeCancel: "إلغاء",
    typeNameAr: "الاسم بالعربية",
    typeNameEn: "الاسم بالإنجليزية",
    typeIcon: "الأيقونة",
    typeCategory: "التصنيف",
    typeFields: "الحقول المتاحة",
    typeFieldsHint: "شاشة «اعرض عقارك» والفلاتر تعرض الحقول المختارة فقط. الأرض مثلًا بلا غرف أو حمامات.",
    typeEnabled: "مفعّل في هذا السوق",
    typeArchived: "مؤرشف",
    typeArchive: "أرشفة",
    typeRestore: "استعادة",
    typeDelete: "حذف",
    typeMoveUp: "تحريك لأعلى",
    typeMoveDown: "تحريك لأسفل",
    typeInUse: "إعلان يستخدم هذا النوع",
    /* Shown INSTEAD of a disabled delete: the rule and the way through it. */
    typeCannotDelete: "لا يمكن حذف نوع تستخدمه إعلانات قائمة. الأرشفة تخفيه من الموقع ومن الإعلانات الجديدة وتترك الإعلانات الحالية كما هي.",
    typeArchiveExplain: "مخفي من الموقع ومن الإعلانات الجديدة. الإعلانات التي تستخدمه لم تتغيّر وما زالت ظاهرة.",
    typeDeleteConfirm: "حذف هذا النوع نهائيًا؟ لا توجد إعلانات تستخدمه.",
    typeCatResidential: "سكني",
    typeCatCommercial: "تجاري",
    typeCatLand: "أراضٍ",
    typeHomeMode: "«تصفّح حسب النوع» في الصفحة الرئيسية",
    typeHomeModeAll: "كل الأنواع المفعّلة",
    typeHomeModeWith: "الأنواع التي بها إعلانات فقط",
    typeHomeModeHint: "النوع الفارغ يفتح صفحة النتائج بحالتها الفارغة الصريحة.",
    fieldArea: "المساحة",
    fieldBeds: "الغرف",
    fieldBaths: "الحمامات",
    fieldKitchens: "المطابخ",
    fieldFurnished: "مفروش",
    fieldFloor: "الدور",
    fieldLandArea: "مساحة الأرض",
    group: "إعدادات الموقع",
    content: "المحتوى",
    home: "الصفحة الرئيسية",
    navigation: "التنقل",
    general: "عام",
    pages: "صفحات المحتوى",
    markets: "الأسواق",
    market: "السوق",
    save: "حفظ",
    saving: "جارٍ الحفظ…",
    saved: "تم حفظ التغييرات",
    allSaved: "كل التغييرات محفوظة",
    unsaved: "تغييرات غير محفوظة",
    saveFailed: "تعذّر الحفظ.",
    /* Operator-facing, and deliberately specific: "save failed" sends someone
       hunting for a mistake they did not make. */
    needsWorker: "هذه الشاشة تحتاج نسخة البروكسي v3.8.6، ولم تُنشر بعد. اقرأ §1 في docs/REBUILD-1-REPORT.md.",
    loadFailed: "تعذّر تحميل الإعدادات.",
    hero: "قسم البداية",
    heroTitle: "العنوان الرئيسي",
    heroSubtitle: "العنوان الفرعي",
    heroImage: "صورة الخلفية",
    heroImageHint: "رابط صورة 16:9. للأجواء فقط، لا تُعرض كإعلان.",
    sections: "أقسام الصفحة",
    sectionsHint: "رتّب الأقسام. القسم المخفي لا يظهر للزوار.",
    visible: "ظاهر",
    moveUp: "لأعلى",
    moveDown: "لأسفل",
    featured: "العقارات المميزة",
    featuredCount: "عدد الإعلانات (١–٨)",
    tagPriority: "أولوية الوسوم",
    listerBand: "شريط المعلنين",
    bandTitle: "العنوان",
    bandBody: "النص",
    bandCta: "نص الزر",
    navItems: "عناصر التنقل",
    navHint: "الاسم يظهر في الهيدر. الوجهة ثابتة في الكود — الرابط ليس محتوى.",
    navCta: "زر الهيدر",
    label: "الاسم",
    destination: "الوجهة",
    noBackend: "غير متاح بعد",
    contact: "بيانات التواصل",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    whatsapp: "واتساب",
    social: "التواصل الاجتماعي",
    countThreshold: "حد إظهار عدد الإعلانات",
    countThresholdHint: "اتركه فارغًا ولن يُعرض أي عدد في أي مكان.",
    pageSlug: "المسار",
    pageLocale: "اللغة",
    pageTitle: "العنوان",
    pageBody: "النص (Markdown)",
    pageMeta: "وصف محرك البحث",
    published: "منشورة",
    newPage: "صفحة جديدة",
    noPages: "لا توجد صفحات بعد.",
    marketName: "الاسم",
    marketSlug: "المسار",
    visibility: "الظهور للزوار",
    visibilityHint: "«ظاهر» يعرض السوق في المبدّل والفوتر. «قريبًا» يعرضه مع علامة قريبًا. «مخفي» يشيله تمامًا ويحوّل روابطه لسوق ظاهر — وبياناته كلها تفضل زي ما هي.",
    visActive: "ظاهر",
    visComingSoon: "قريبًا",
    visHidden: "مخفي",
    visLastOne: "ده آخر سوق ظاهر — لازم يفضل سوق واحد على الأقل ظاهر للزوار.",
    marketActive: "مفعّل",
    currency: "العملة",
    currencySymbol: "الرمز",
    currencyFormat: "صيغة السعر",
    currencyDisplay: "عرض العملة",
    currencyFormatHint: "استخدم {n} مكان الرقم — مثال: {n} ج.م أو EGP {n}",
    currencyNeedsN: "الصيغة لازم تحتوي {n} مكان الرقم.",
    timezone: "المنطقة الزمنية",
    phoneCode: "مفتاح الدولة",
    tax: "الضريبة ٪",
    gateway: "بوابة الدفع",
    requiresLicence: "يتطلب ترخيص إعلاني",
    featureProjects: "المشاريع",
    featureAdvisor: "المستشار الذكي",
    texts: "نصوص الموقع",
    textsHint: "كل نص ظاهر على الموقع. الفارغ يعني النص الأصلي من التصميم.",
    searchTexts: "ابحث في النصوص…",
    onlyEdited: "المعدّلة فقط",
    value: "النص",
    original: "الأصل",
    resetToDefault: "رجوع للأصل",
    maxChars: "الحد {n} حرف",
    tooLong: "أطول من الحد بـ {n} حرف.",
    noMarkup: "نص عادي فقط — بدون وسوم HTML.",
    editedBy: "آخر تعديل: {who} · {when}",
    notEdited: "لم يُعدَّل",
    nothingFound: "لا توجد نصوص مطابقة.",
    preview: "معاينة",
    previewAfterSave: "المعاينة تعرض الموقع كما هو الآن — تتحدث بعد الحفظ.",
    previewMobile: "جوّال",
    previewDesktop: "سطح المكتب",
    textsCount: "{n} نص",
    projectsDisabled: "لا يوجد باك-إند للمشاريع — البروكسي يرفض تفعيلها.",
  },
  /* L1a — the listing lifecycle: the settings screen, the rejection reasons, the
     review queue, the cron tile, and what the public sees of it. Every number in
     the lifecycle is a setting; every WORD here is a default a site_texts row
     overrides. */
  lifecycle: {
    crumb: "الأسواق",
    settingsTitle: "الإعلانات",
    settingsHint: "كل رقم هنا خاص بسوق واحد ويُعدَّل من هنا. لا توجد قيمة احتياطية: سطر ناقص يظهر كخطأ ولا يُخمَّن.",
    notSeeded: "هذه الإعدادات لم تُهيَّأ لهذا السوق بعد (تحتاج تطبيق الترحيل 015).",
    badValue: "القيمة لا تناسب هذا الإعداد.",
    unlimited: "بلا حد",
    groupReview: "المراجعة",
    groupCycle: "دورة الإعلان",
    groupLimits: "الحدود وكشف التكرار",
    reviewMode: "وضع المراجعة",
    reviewModeHint: "«كل الإعلانات» تراجع كل إعلان جديد. الوضعان الآخران محفوظان لكنهما غير مفعّلين بعد.",
    modeAll: "كل الإعلانات",
    modeFirstOnly: "أول إعلان فقط",
    modeTrustedAuto: "تلقائي للموثّقين",
    availabilityCheckDays: "سؤال «لسه متاح؟» بعد (يوم)",
    autoPauseSilentDays: "إيقاف مؤقت بعد صمت (يوم)",
    expiredGraceDays: "مهلة التجديد بعد الانتهاء (يوم)",
    archiveRestoreDays: "مهلة استرجاع الأرشيف (يوم)",
    freeDurationDays: "مدة الإعلان المجاني (يوم)",
    freeCeiling: "سقف الإعلانات المفعّلة — حساب غير موثّق",
    verifiedCeiling: "سقف الإعلانات المفعّلة — وسيط موثّق",
    verifiedCeilingHint: "فارغ = بلا سقف.",
    duplicateWindowDays: "نافذة كشف التكرار (يوم)",
    duplicateMatchFields: "حقول المطابقة",
    fieldPhone: "رقم الهاتف",
    fieldDistrict: "المنطقة",
    fieldPriceBand: "نطاق السعر",
    priceBandPercent: "نطاق السعر ± (٪)",
    refreshMinHours: "أقل فاصل بين تحديثين (ساعة)",
    reasonsCrumb: "الأسواق",
    reasonsTitle: "أسباب الرفض",
    reasonsHint: "تظهر هذه الأسباب عند رفض إعلان. الأرشفة تخفي السبب من القائمة دون المساس بالإعلانات التي استعملته.",
    reasonKey: "المعرّف",
    reasonKeyHelp: "حروف لاتينية صغيرة وأرقام وشرطة سفلية، ولا يتغيّر بعد الإضافة.",
    reasonLabelAr: "النص بالعربية",
    reasonLabelEn: "النص بالإنجليزية",
    reasonOrder: "الترتيب",
    reasonAdd: "إضافة سبب",
    reasonArchive: "أرشفة",
    reasonActivate: "تفعيل",
    reasonArchived: "مؤرشف",
    reasonsEmpty: "لا توجد أسباب رفض لهذا السوق بعد.",
    reviewCrumb: "الإعلانات",
    reviewTitle: "قائمة المراجعة",
    reviewEmpty: "لا إعلانات بانتظار المراجعة.",
    reviewApprove: "موافقة",
    reviewReject: "رفض",
    reviewPickReason: "اختر سبب الرفض",
    reviewNote: "ملاحظة للمعلن (اختيارية)",
    reviewConfirmReject: "تأكيد الرفض",
    reviewCancel: "إلغاء",
    reviewDuplicate: "مشتبه في تكراره",
    reviewMatches: "يشبه هذا الإعلان:",
    reviewApproved: "تمت الموافقة ✓",
    reviewRejected: "تم الرفض ✓",
    reviewNeedsReason: "اختر سبب الرفض أولًا.",
    reviewFailed: "تعذّر تنفيذ الإجراء.",
    reviewChanged: "تغيّر الإعلان؛ حدّث القائمة.",
    cronTitle: "آخر تشغيل للمهام الدورية",
    cronStarted: "بدأ",
    cronExpired: "انتهت صلاحيتها",
    cronArchived: "أُرشفت",
    cronNudged: "سُئلوا «لسه متاح؟»",
    cronPaused: "أُوقفت تلقائيًا",
    confirmedToday: "تم التأكيد اليوم",
    confirmedAgo: "تم التأكيد قبل {n} يوم",
    ceilingTitle: "وصلت إلى الحد الأقصى للإعلانات",
    ceilingBody: "يمكنك الاحتفاظ بـ {n} إعلانًا مفعّلًا أو قيد المراجعة في الوقت نفسه. أوقف أو أرشف إعلانًا قديمًا لتنشر جديدًا، أو وثّق حسابك لرفع الحد.",
    ceilingVerify: "وثّق حسابك مجانًا",
  },
  results: {
    filters: "الفلاتر",
    purpose: "الغرض",
    buy: "شراء",
    rent: "إيجار",
    cityAndArea: "المدينة والمنطقة",
    city: "المدينة",
    anyCity: "كل المدن",
    area: "المنطقة",
    anyArea: "كل المناطق",
    propertyType: "نوع العقار",
    price: "السعر",
    from: "من",
    to: "إلى",
    rooms: "الغرف",
    any: "أي",
    videoOnly: "جولة فيديو فقط",
    clearFilters: "مسح الفلاتر",
    sort: "ترتيب",
    sortNewest: "الأحدث",
    sortPriceAsc: "السعر: من الأقل",
    sortPriceDesc: "السعر: من الأعلى",
    sortArea: "المساحة",
    view: "طريقة العرض",
    viewList: "قائمة",
    viewMap: "خريطة",
    home: "الرئيسية",
    /* «شقق للبيع في القاهرة» in the design. The parts are assembled from what
       the visitor actually filtered by; with no filters it is the plain form. */
    titleAll: "عقارات",
    titleFor: "{types} {purpose}",
    titleIn: "{what} في {where}",
    forSale: "للبيع",
    forRent: "للإيجار",
    emptyTitle: "لا توجد نتائج مطابقة",
    emptyBody: "جرّب توسيع المنطقة أو إزالة بعض الفلاتر.",
    /* An area that simply has nothing in it YET -- not a failed search. The
       home page shows an area as soon as it has a photograph, so these pages
       exist on purpose, and «جرّب إزالة بعض الفلاتر» would be untrue: there is
       no filter to remove. */
    emptyAreaTitle: "لسه مفيش إعلانات في {area}",
    emptyAreaBody: "كن أول من يعرض عقاره هنا.",
    emptyAreaCta: "اعرض عقارك",
    error: "تعذّر تحميل النتائج. تحقق من الاتصال وحاول مرة أخرى.",
    loading: "جارٍ تحميل النتائج",
    pages: "الصفحات",
    prev: "السابق",
    next: "التالي",
    page: "صفحة {n}",
    apply: "تطبيق",
    close: "إغلاق",
  },
  /* L1b — DASHBOARD-2: «إعلاناتي» and the lister's dashboard, transcribed from
     docs/design/v6-lifecycle (my-listings-v2-*, dashboard-v2-*, lifecycle-1440).
     Copy the package does not draw (emptyTabBody, errorTitle, attnNotSetUp,
     threadsEmpty) is the smallest honest sentence and Admin → Site Texts can
     replace it. The marker used to sit INSIDE the value and reached the screen
     (L1b review F1) -- provenance lives in this comment now. */
  myListings: {
    eyebrow: "إعلاناتي",
    title: "إعلاناتي",
    search: "ابحث في إعلاناتك…",
    listProperty: "اعرض عقارك",
    tabAll: "الكل",
    tabActive: "نشط",
    tabPending: "قيد المراجعة",
    tabRejected: "مرفوض",
    tabPaused: "موقوف",
    tabExpired: "منتهي",
    tabSold: "تم البيع",
    tabArchived: "مؤرشف",
    tabDraft: "مسودة",
    colListing: "الإعلان",
    colStatus: "الحالة",
    colViews: "مشاهدات · محفوظات",
    colConversations: "محادثات",
    colActions: "الإجراءات",
    saves: "{n} حفظ",
    /* status chips (the design's words) */
    stActive: "نشط",
    stPending: "قيد المراجعة",
    stRejected: "مرفوض",
    stPaused: "موقوف",
    stPausedByLimit: "موقوف",
    stExpired: "منتهي",
    stSold: "تم البيع",
    stRented: "تم التأجير",
    stArchived: "مؤرشف",
    stDraft: "مسودة",
    /* meaning lines */
    daysLeft: "باقي {n} يومًا",
    daysLeftOne: "باقي يوم واحد",
    endsToday: "ينتهي اليوم",
    rejectedWhen: "مرفوض {when}",
    rejectedNoWhen: "مرفوض",
    pendingSince: "قيد المراجعة منذ {when}",
    pausedSince: "موقوف منذ {when}",
    pausedUnconfirmed: "موقوف — لم يُؤكَّد توافره",
    pausedByLimit: "موقوف — تجاوز الحد",
    expiredOn: "انتهى {when}",
    soldOn: "بيعت {when}",
    rentedOn: "أُجّرت {when}",
    archivedOn: "أُرشفت {when}",
    draftMeaning: "لم يُرسل بعد",
    /* actions (titles) */
    actView: "عرض",
    actEdit: "تعديل",
    actComplete: "أكمل",
    hoursAgo: "{h} ساعة",
    daysAgo: "{d} يوم",
    actRefresh: "تحديث",
    actPause: "إيقاف",
    actResume: "استئناف",
    actRenew: "جدّد",
    actSold: "تم البيع",
    actRented: "تم التأجير",
    actShare: "مشاركة",
    actArchive: "أرشفة",
    actRestore: "استرجاع",
    actConfirm: "لسه متاح ✓",
    actResubmit: "عدّل وابعته تاني",
    actMore: "المزيد",
    actionsLegend: "الإجراءات: عرض · تعديل · تحديث · إيقاف/استئناف · جدّد · تم البيع/التأجير · مشاركة · أرشفة — الرمادي = غير متاح في هذه الحالة.",
    /* sold / rented modal */
    soldEyebrow: "تم البيع",
    rentedEyebrow: "تم التأجير",
    soldTitle: "مبروك! «{title}» اتباعت؟",
    rentedTitle: "مبروك! «{title}» اتأجّرت؟",
    soldBody: "هنوقف الرسائل ونعلّمها «تم البيع» للباحثين. المشاهدات والإحصائيات تبقى في حسابك.",
    rentedBody: "هنوقف الرسائل ونعلّمها «تم التأجير» للباحثين. المشاهدات والإحصائيات تبقى في حسابك.",
    priceLabel: "اتباعت بكام؟",
    priceLabelRent: "اتأجّرت بكام؟",
    priceHint: "(اختياري — خاص، لا يُنشر، يحسّن نصيحة السعر لك وللمنطقة)",
    cancel: "إلغاء",
    soldConfirm: "تم البيع ✓",
    rentedConfirm: "تم التأجير ✓",
    /* archive modal */
    archiveTitle: "أرشفة «{title}»؟",
    archiveBody: "يختفي من نتائج البحث ويقلّل عدد إعلاناتك النشطة. تقدر ترجعه من تبويب «مؤرشف» — ويرجع للمراجعة بمشاهداته.", // review F3, Bassem «تمام» 2026-11-03
    archiveConfirm: "أرشف",
    /* share sheet */
    shareTitle: "مشاركة الإعلان",
    shareCopy: "نسخ",
    shareCopied: "تم النسخ",
    shareWhatsapp: "واتساب",
    shareFacebook: "فيسبوك",
    /* toasts */
    toastRefreshed: "تم تحديث ترتيب الإعلان — يظهر كأنه نُشر الآن",
    toastPaused: "تم إيقاف الإعلان",
    toastResumed: "رجع الإعلان يظهر للباحثين",
    toastRenewed: "تم التجديد — {n} يومًا جديدة",
    toastArchived: "تمت الأرشفة",
    toastRestored: "رجع للمراجعة — هينشر تاني بعد الموافقة",
    toastSold: "تم تعليم الإعلان «تم البيع»",
    toastRented: "تم تعليم الإعلان «تم التأجير»",
    toastConfirmed: "شكرًا — أكّدنا إن الإعلان لسه متاح",
    undo: "تراجع",
    /* the server's answers */
    errStale: "الإعلان اتغيّر في الأثناء — حدّثنا القائمة.",
    errCeiling: "وصلت لحد {ceiling} إعلانات نشطة. وثّق حسابك مجانًا لرفع الحد.",
    errCeilingLink: "وثّق حسابك مجانًا",
    errTooSoon: "تقدر تحدّث تاني بعد {n} ساعة.",
    errNotIncluded: "باقتك لا تشمل التحديث.",
    errUpgrade: "رقّي باقتك",
    errExpired: "الإعلان منتهي — جدّده الأول.",
    errRenewWindow: "فات وقت التجديد — استرجعه من «مؤرشف».",
    errRestoreWindow: "فات وقت الاسترجاع.",
    errPhotos: "الإعلان محتاج {need} صور على الأقل قبل النشر (عندك {have}).",
    errNotSetUp: "الإعدادات غير مكتملة — تواصل مع الدعم.",
    errGeneric: "لم ينجح الإجراء — جرّب تاني.",
    /* empty states */
    emptyAllTitle: "لسه ماعرضتش عقار",
    emptyAllBody: "أول إعلان لك يظهر هنا بحالته وإحصائياته.",
    emptyTabTitle: "لا إعلانات هنا",
    emptyTabBody: "ما فيش إعلانات في الحالة دي دلوقتي.",
    emptySoldTitle: "لا إعلانات مباعة بعد",
    emptySoldBody: "لما تقفل صفقة اضغط «تم البيع» على الإعلان — يتحوّل هنا ويوقف الرسائل.",
    errorTitle: "تعذّر تحميل إعلاناتك",
    retry: "حاول تاني",
    loading: "جارٍ التحميل…",
  },
  /* L1b — the lister's dashboard, dashboard-v2-* and lifecycle-1440 (moments 3–5, 7). */
  dash: {
    eyebrow: "لوحة التحكم",
    hello: "أهلًا {name} 👋",
    listProperty: "اعرض عقارك",
    attnTitle: "محتاج منك دلوقتي",
    attnCount: "{n} عناصر",
    attnClear: "كله تمام",
    attnClearTitle: "مافيش حاجة مستعجلة",
    attnClearBody: "إعلاناتك سارية. آخر فحص {when}.",
    attnNotSetUp: "قائمة الانتباه غير مُعدّة بعد — تواصل مع الدعم.",
    justNow: "قبل لحظات",
    rejectedTitle: "إعلان مرفوض: «{title}»",
    rejectedBody: "السبب: {reason}",
    rejectedNoReason: "راجع الإعلان وعدّله",
    resubmit: "عدّل وابعته تاني",
    expiringTitle: "إعلان بينتهي بعد {n} أيام",
    expiringTitleOne: "إعلان بينتهي بكرة",
    expiringTitleToday: "إعلان بينتهي النهارده",
    expiringBody: "«{title}» — لو لسه متاح جدّده بضغطة، بنفس المشاهدات والمحفوظات.",
    expiringDangerBody: "بعد الانتهاء يختفي من البحث و{saves} شخص حفظوه يشوفوه «منتهي». جدّده بضغطة.",
    renew: "جدّد",
    renewNow: "جدّد الآن",
    markSold: "تم البيع",
    markRented: "تم التأجير",
    markOutcome: "تم البيع/التأجير",
    availTitle: "لسه متاح؟ «{title}»",
    availBody: "مرّ {n} يومًا بدون تأكيد — أكّد التوفر ليظل الإعلان ظاهرًا بشارة «تم التأكيد»",
    confirmAvailable: "لسه متاح ✓",
    quotaTitle: "استخدمت {used} من {ceiling} إعلانًا",
    quotaBody: "خانة واحدة متبقية — أرشف إعلانًا قديمًا أو وثّق حسابك مجانًا لرفع الحد.",
    quotaBodyMany: "{left} خانات متبقية — أرشف إعلانًا قديمًا أو وثّق حسابك مجانًا لرفع الحد.",
    archiveOld: "أرشف القديم",
    verify: "وثّق حسابك",
    /* plan card */
    planFree: "الباقة المجانية",
    planRenews: "تتجدد تلقائيًا في {date} · {price}",
    planManage: "إدارة",
    planFreeLine: "بلا تجديد · الإعلان مجاني",
    usageActive: "الإعلانات النشطة",
    usageActiveNoLimit: "{n} إعلانات نشطة",
    usageAi: "استشارات المستشار الذكي",
    usageAiLeft: "متبقي {n} استشارة",
    usageAiUnlimited: "بلا حد",
    usageAiNone: "غير متاحة في المجانية",
    upgrade: "رقّي باقتك",
    upgradeFrom: "رقّي — من {price}",
    comparePlans: "مقارنة الباقات",
    /* listings section */
    listingsTitle: "إعلاناتك",
    allListings: "كل الإعلانات ←",
    /* performance */
    perfTitle: "أداءك",
    statViews: "إجمالي المشاهدات",
    statViewsSub: "على {n} إعلانات نشطة",
    statSaves: "المحفوظات",
    statSavesSub: "أضافوا إعلاناتك للمفضلة",
    statViewsZero: "المشاهدات تبدأ بعد النشر",
    statSavesZero: "المحفوظات تبدأ بعد النشر",
    statMsgsZero: "الرسائل تظهر هنا",
    threadsTitle: "أحدث المحادثات",
    threadsAll: "الكل ←",
    threadsEmpty: "لا محادثات بعد.",
    /* first-time */
    firstTitle: "اعرض أول عقار لك",
    firstBody: "٤ خطوات، أقل من ٥ دقائق.",
    /* error */
    errorTitle: "تعذّر تحميل لوحة التحكم",
    errorBody: "إعلاناتك بخير — المشكلة في العرض فقط.",
    retry: "أعد المحاولة",
  },
  /* WIZARD-2 — «اعرض عقارك» on docs/design/v4-wizard (wizard-step1…4, success).
     AR as drawn; every number is a bind ({max}, {min}, {n}) filled from the
     market's rows (migration 018) or the server. Copy the package does not draw
     is named in DEVIATIONS § WIZARD-2. */
  wizard2: {
    pageTitle: "اعرض عقارك",
    saveExit: "حفظ والخروج",
    saveDraftExit: "حفظ كمسودة والخروج",
    back: "السابق",
    nextTo: "التالي: {name}",
    submit: "أرسل للمراجعة",
    resubmit: "أعد الإرسال للمراجعة",
    stepOfM: "الخطوة {n} من {total} — {name}",
    nextM: "التالي: {name}",
    steps: [
      { name: "العقار والموقع", sub: "النوع، الموقع، خصوصية الموقع" },
      { name: "التفاصيل والسعر", sub: "المساحة والغرف والسعر والوصف" },
      { name: "الصور والفيديو", sub: "{min} على الأقل — الصورة الأولى هي الغلاف" },
      { name: "المراجعة والنشر", sub: "راجع الإعلان ثم أرسله للمراجعة" },
    ],
    saved: "تم الحفظ تلقائيًا · {when}",
    saving: "جارٍ الحفظ…",
    saveFailed: "تعذّر الحفظ — سنعيد المحاولة",
    notSavedYet: "يُحفظ تلقائيًا أثناء الكتابة",
    justNow: "الآن",
    restored: "استعدنا مسودتك من",
    restoredTail: "— أكمل من حيث توقفت.",
    startOver: "ابدأ من جديد",
    startOverTitle: "تبدأ من جديد؟",
    startOverBody: "هتتنقل المسودة الحالية لتبويب «مؤرشف» في إعلاناتي، وتبدأ إعلانًا فارغًا.",
    startOverYes: "ابدأ من جديد",
    cancel: "إلغاء",
    // step 1
    s1eyebrow: "Step 1 · Property & location",
    s1title: "ما العقار الذي تعرضه؟",
    purpose: "نوع الإعلان",
    forSale: "للبيع",
    forRent: "للإيجار",
    propType: "نوع العقار",
    whereTitle: "أين يقع العقار؟",
    whereSubManual: "اختر المدينة والحي من القوائم، واكتب العنوان التفصيلي.",
    country: "الدولة",
    city: "المدينة",
    district: "الحي",
    pickCity: "اختر المدينة…",
    pickDistrict: "اختر الحي…",
    errPickDistrict: "اختر الحي",
    errPickCity: "اختر المدينة",
    errPickType: "اختر نوع العقار",
    address: "العنوان التفصيلي",
    addressNote: "(يظهر للباحثين بحسب خصوصية الموقع)",
    addressPlaceholder: "الشارع، رقم العمارة، علامة مميزة",
    privacy: "خصوصية الموقع:",
    precise: "الموقع الدقيق",
    approx: "المنطقة التقريبية فقط",
    approxHint: "يرى الباحثون دائرة تقريبية بلا رابط اتجاهات — العنوان الكامل يُرسل عند التواصل.",
    preciseHint: "يظهر الدبوس الدقيق مع رابط الاتجاهات.",
    locateToContinue: "حدّد موقع العقار للمتابعة",
    // step 2
    s2eyebrow: "Step 2 · Details & price",
    s2title: "التفاصيل والسعر",
    titleLabel: "عنوان الإعلان",
    titleNote: "(مطلوب · حتى {max} حرفًا)",
    titlePlaceholder: "مثال: شقة ٣ غرف بحديقة خاصة في الحي الأول",
    errTitle: "اكتب عنوانًا للإعلان",
    areaLabel: "المساحة",
    landAreaLabel: "مساحة الأرض",
    sqm: "م²",
    beds: "غرف النوم",
    baths: "الحمامات",
    kitchens: "المطابخ",
    furnished: "مفروشة؟",
    yes: "نعم",
    no: "لا",
    finishing: "حالة التشطيب",
    finishingPick: "اختر…",
    finishingLevels: { core_shell: "على الطوب", semi: "نصف تشطيب", lux: "لوكس", super_lux: "سوبر لوكس", ultra_lux: "ألترا سوبر لوكس" },
    priceTitle: "السعر",
    priceLabel: "السعر المطلوب",
    required: "(مطلوب)",
    perMonth: "/ شهريًا",
    errPrice: "أدخل السعر ({currency})",
    errArea: "أدخل المساحة",
    options: "خيارات",
    negotiable: "قابل للتفاوض",
    installments: "تقسيط متاح",
    deposit: "تأمين شهر",
    descTitle: "الوصف",
    descSub: "{min} حرفًا على الأقل — اذكر المرافق القريبة والمميزات الفريدة.",
    descPlaceholder: "صف العقار والمرافق القريبة وأي مميزات خاصة…",
    descCounter: "{n} / {min} حرفًا على الأقل",
    // side
    previewEyebrow: "Live preview · معاينة",
    previewSub: "كما سيظهر للباحثين",
    previewNoPhoto: "ستظهر الصورة الرئيسية هنا",
    previewPrice: "السعر",
    previewTitle: "عنوان الإعلان",
    tipsEyebrow: "Tips · نصائح",
    tips1: ["«المنطقة التقريبية» تحمي عنوانك وتكفي للباحث في المرحلة الأولى.", "الدبوس الدقيق يرفع الثقة للفلل والعقارات التجارية."],
    tips2: ["العنوان الواضح يذكر النوع والغرف والمنطقة.", "السعر الحقيقي يجيب رسائل أكثر من السعر المنخفض المضلّل."],
    // states
    signInTitle: "سجّل الدخول لتعرض عقارك",
    signInBody: "الإعلان بيتحفظ في حسابك كمسودة لحد ما ترسله للمراجعة.",
    signIn: "تسجيل الدخول",
    loading: "جارٍ تحميل المسودة…",
    loadFailed: "تعذّر تحميل المسودة",
    retry: "أعد المحاولة",
    limitsNotSetUp: "حدود الإعلانات غير مُعدّة لهذا السوق ({key}). أبلغ الإدارة.",
    notYourDraft: "المسودة دي مش موجودة أو مش في حسابك.",
    // step 3
    s3eyebrow: "Step 3 · Photos & video",
    s3title: "الصور والفيديو",
    s3sub: "اسحب لإعادة الترتيب — الأولى هي الغلاف.",
    dropTitle: "اسحب الصور هنا أو اضغط للاختيار",
    dropSub: "{min} على الأقل · JPG أو PNG أو WEBP · حتى {mb} ميجابايت · الصورة الأولى تصبح الغلاف",
    choosePhotos: "اختر الصور",
    addPhoto: "أضف صورة",
    cover: "COVER · غلاف",
    coverShort: "غلاف",
    ruleMin: "{min} على الأقل ({n}/{k})",
    /* Arabic number agreement for a count that is a row (listings.minPhotos). */
    photoCount: { one: "صورة واحدة", two: "صورتان", few: "{n} صور", many: "{n} صورة" },
    ruleMax: "حتى {max} صورة",
    ruleTypes: "JPG · PNG · WEBP",
    ruleMb: "حتى {mb} ميجابايت للصورة",
    rulePx: "العرض {px} بكسل على الأقل",
    maxReached: "وصلت للحد الأقصى: {max} صورة. احذف صورة لإضافة أخرى.",
    failNetwork: "فشل الرفع",
    failTooLarge: "أكبر من {mb} ميجابايت",
    failBadType: "نوع غير مدعوم",
    failTooSmall: "أصغر من {px} بكسل",
    retryUpload: "أعد المحاولة",
    pickAnother: "اختر صورة أخرى",
    removePhoto: "احذف الصورة {n}",
    moveEarlier: "انقل الصورة {n} للأمام",
    moveLater: "انقل الصورة {n} للخلف",
    makeCover: "اجعلها الغلاف",
    photosNeeded: "أضف {min} على الأقل للمتابعة",
    photosUploading: "استنى لحد ما الصور تخلص رفع",
    videoTitle: "جولة فيديو",
    optional: "(اختياري)",
    videoLabel: "رابط يوتيوب",
    videoHint: "الصق رابط فيديو على يوتيوب — يظهر داخل الإعلان.",
    videoBad: "الرابط ده مش رابط فيديو يوتيوب",
    videoOk: "✓ الفيديو هيظهر في الإعلان",
    videoRemove: "إزالة",
    tips3: ["صوّر بالضوء الطبيعي وافتح الستائر.", "اجعل الغلاف الصالة أو الواجهة — لا الحمام."],
    // step 4
    s4eyebrow: "Step 4 · Review & submit",
    s4title: "راجع إعلانك قبل الإرسال",
    s4sub: "بعد الإرسال يراجعه فريقنا خلال {n} ساعة ونبلّغك فور نشره.",
    rowPurpose: "نوع الإعلان",
    rowLocation: "الموقع",
    rowTitle: "العنوان",
    rowDetails: "التفاصيل",
    rowPrice: "السعر",
    rowDescription: "الوصف",
    rowMedia: "الصور والفيديو",
    edit: "تعديل",
    roomsN: "{n} غرف",
    bathsN: "{n} حمام",
    kitchensN: "{n} مطبخ",
    furnishedYes: "مفروشة",
    furnishedNo: "غير مفروشة",
    withVideo: "فيديو",
    none: "—",
    incomplete: "خطوة «{name}» ناقصة — كمّلها قبل الإرسال.",
    goFix: "روح للخطوة",
    terms: "أؤكد أنني مالك العقار أو مفوَّض بعرضه، وأن البيانات صحيحة. أوافق على",
    termsLink: "شروط النشر",
    termsNeeded: "وافق على شروط النشر للمتابعة",
    disclosure: "سيظهر اسمك {name} على الإعلان. رقم هاتفك لا يُنشر — التواصل عبر رسائل سوق عقار.",
    disclosureVerified: "سيظهر اسمك {name} وشارة {badge} على الإعلان. رقم هاتفك لا يُنشر — التواصل عبر رسائل سوق عقار.",
    verifiedBadge: "وسيط معتمد ✓",
    submitting: "جارٍ الإرسال…",
    submitError: "تعذّر إرسال الإعلان — لم يُفقد شيء، مسودتك محفوظة. تحقق من الاتصال وأعد المحاولة.",
    quotaReached: "وصلت للحد الأقصى من الإعلانات في باقتك. سنحفظ الإعلان كمسودة — رقِّ باقتك لنشره.",
    upgrade: "رقّي باقتك",
    photosRefused: "الإعلان محتاج {min} صور على الأقل قبل الإرسال — عندك {have}.",
    stale: "حالة الإعلان اتغيّرت من مكان تاني. حدّث الصفحة.",
    reload: "حدّث",
    rejectedBanner: "سبب الرفض السابق: {reason} — تأكد من معالجته قبل إعادة الإرسال.",
    rejectedDefault: "لم يستوفِ الإعلان شروط النشر. راجع الصور والسعر والوصف.",
    tips4: ["اقرا العنوان والسعر كأنك الباحث.", "تقدر تعدّل الإعلان وهو قيد المراجعة."],
    // success
    successEyebrow: "Submitted",
    successTitle: "استلمنا إعلانك — قيد المراجعة الآن",
    resubmittedEyebrow: "Resubmitted",
    resubmittedTitle: "أعدنا إرسال إعلانك للمراجعة",
    successBody: "يراجعه فريقنا عادةً خلال {n} ساعة. هنبلّغك أول ما يتنشر — عبر رسائل سوق عقار.",
    pending: "قيد المراجعة",
    viewInMine: "عرض في إعلاناتي",
    listAnother: "+ اعرض عقارًا آخر",
    dashboard: "لوحة التحكم",
    editWhilePending: "يمكنك التعديل أثناء المراجعة دون إعادة الإرسال.",
    meanwhileEyebrow: "Meanwhile · في الأثناء",
    meanwhile: ["شارك الإعلان مع معارفك بعد النشر من زر «مشاركة».", "الرسائل تصلك في «الرسائل» — الرد السريع بيفرق."],
    comingNext: "الخطوة دي جاية في الإصدار الجاي من المعالج.",
  },
};

export type V3Strings = typeof ar;

const en: V3Strings = {
  header: {
    main: "Main",
    menu: "Menu",
    login: "Log in",
    listProperty: "List your property",
    otherLocale: "العربية",
    otherLocaleLang: "ar",
  },
  nav: {
    buy: "Buy",
    rent: "Rent",
    projects: "New projects",
    advisor: "AI advisor",
    plans: "Plans",
  },
  marketComingSoon: {
    tag: "Coming soon",
    title: "{market} \u2014 coming soon.",
    body: "We have not opened this market yet, so there are no properties here.",
    go: "Browse {market}",
  },
  marketSuggestion: {
    lead: "It looks like you are in {market}.",
    leadLong: "It looks like you are in {market}. Browse the {market} market?",
    go: "Go to /{slug}/",
    goShort: "Browse the {market} market",
    close: "Close",
  },
  hero: {
    title: "Your next home starts here",
    subtitle: "Search apartments, villas, chalets and new projects, and contact listers directly.",
    tabBuy: "Buy",
    tabRent: "Rent",
    tabProjects: "Projects",
    location: "Location",
    locationPlaceholder: "City or area",
    locationUnknown: "No city or area by that name. Pick one from the list.",
    priceMin: "From",
    priceMax: "To",
    type: "Type",
    typeAny: "All types",
    price: "Price",
    priceAny: "Any price",
    rooms: "Rooms",
    roomsAny: "Any",
    search: "Search",
  },
  sections: {
    browseTypes: "Browse by type",
    allAreas: "All areas \u2192",
    allTypes: "All types →",
    browseAreas: "Browse by area",
    featured: "Featured properties",
    allListings: "All properties →",
    allListingsShort: "All",
    plans: "Plans for listers",
    plansHint: "Names, prices and limits come from the database",
    plansHintShort: "Data comes from the database",
  },
  types: {
    Apartment: "Apartment",
    Villa: "Villa",
    Duplex: "Duplex",
    Studio: "Studio",
    Chalet: "Chalet",
  },
  listing: {
    forSale: "For sale",
    forRent: "For rent",
    video: "Video",
    save: "Save",
    unsave: "Remove from saved",
    beds: "beds",
    baths: "baths",
    sqm: "m²",
    million: "M",
    thousand: "K",
    /* The card says "/ mo" in the design; the plan card says "/ month".
       Two strings because the design uses two. */
    perMonth: "/ mo",
  },
  featuredStates: {
    loading: "Loading featured properties",
    emptyTitle: "No featured properties yet",
    emptyBody: "Search by area or type, or be the first to list a property.",
    emptyCta: "List your property",
    error: "Featured properties could not be loaded.",
    retry: "Try again",
  },
  advisor: {
    badge: "AI advisor",
    title: "Ask before you decide",
    body: "The advisor reads the listing and compares it with the area: Is the price fair? Buy or rent? What is the outlook for the neighbourhood?",
    bodyShort: "Is the price fair? Buy or rent? The advisor reads the listing and compares it with the area.",
    cta: "Try the advisor",
    sampleLabel: "Illustrative example",
    q1: "Is this price fair for a 185 m² apartment in the 5th Settlement?",
    a1: "Compared with similar listings in the area, it sits within the usual range. If it is ready to move in and finished, the price is reasonable.",
    q2: "Should I buy or rent if I am only staying 3 years?",
    composerPlaceholder: "Ask about any listing…",
    send: "Send",
  },
  listerBand: {
    title: "Have a property to sell or rent?",
    body: "Publish in minutes and receive messages from interested buyers directly, with no middleman.",
    bodyShort: "Publish in minutes and receive messages from interested buyers directly.",
    cta: "List your property",
  },
  plan: {
    listings: "Listings:",
    featured: "Featured:",
    aiCredits: "Advisor credits:",
    choose: "Choose plan",
    error: "Plans could not be loaded.",
    unlimited: "Unlimited",
    free: "Free",
    perMonth: "/ month",
  },
  footer: {
    label: "Footer",
    markets: "Markets:",
    copyright: "© {year} SouqAkar",
    about: "About",
    contact: "Contact",
    faq: "FAQ",
    terms: "Terms",
    privacy: "Privacy",
  },
  marketSwitcher: {
    label: "Choose market",
  },
  /* NO EN FILE IN THE PACKAGE for this screen -- the handoff ships results
     in Arabic only. This English is ours, written to the Arabic's sense, and
     it is flagged here rather than passed off as the design's. */
  /* Arabic-only screen in the package; this English is ours. */
  detail: {
    photos: "Photos",
    photoCount: "{n} photos",
    videoTour: "Video tour",
    save: "Save",
    saved: "Saved",
    share: "Share",
    shared: "Link copied",
    publishedRef: "Listing no.",
    type: "Type",
    area: "Area",
    sqm: "m²",
    beds: "Bedrooms",
    baths: "Bathrooms",
    kitchens: "Kitchens",
    furnished: "Furnished",
    yes: "Yes",
    no: "No",
    licence: "Advertising licence no.",
    description: "Description",
    location: "Location",
    locationApprox: "Only the approximate location is shown until you contact the lister.",
    play: "Play video",
    contact: "Contact the lister",
    contactHint: "You talk directly inside the site — no middleman, no fees.",
    memberSince: "Member since",
    advisorTitle: "Is this a fair price for the area?",
    advisorBody: "Ask about this listing: price against the area, buy or rent, and the outlook.",
    advisorCta: "Ask the advisor",
    similar: "Similar properties",
    more: "More →",
    goneTitle: "This listing is no longer available",
    goneBody: "It may have been sold or taken down. Browse similar properties in the same area.",
    goneCta: "Similar properties",
    error: "The listing could not be loaded. Try again.",
    priceOnRequest: "Price on request",
  },
  /* Arabic-only screen in the package; this English is ours. */
  post: {
    steps: ["Type", "Location", "Details", "Photos", "Price", "Review"],
    stepsLabel: "Listing steps",
    stepOf: "Step {n} of {total}",
    saveDraft: "Save as draft",
    draftSaved: "Draft saved on this device",
    autosaved: "Last autosave",
    now: "just now",
    prev: "Back",
    next: "Next",
    publish: "Publish listing",
    purpose: "Purpose",
    forSale: "For sale",
    forRent: "To rent",
    t1: "Property type and purpose",
    t1hint: "Pick the purpose, then the property type.",
    t2: "Where the property is",
    t2hint: "The city and area appear on the listing. An exact location is optional.",
    city: "City",
    area: "Area",
    pickCity: "Choose a city",
    pickArea: "Choose an area",
    preciseLabel: "Show an exact point on the map",
    preciseHint: "Leave it off and only an approximate area is shown.",
    t3: "Property details",
    t3hint: "Everything below appears on the listing page.",
    sqm: "Area (m²)",
    landArea: "Land area (m²)",
    beds: "Bedrooms",
    baths: "Bathrooms",
    kitchens: "Kitchens",
    furnished: "Furnished",
    yes: "Yes",
    no: "No",
    title: "Listing title",
    titlePlaceholder: "For example: 3-bed apartment with a private garden",
    titleHint: "Short and clear. No phone numbers, no sales language.",
    description: "Description",
    descriptionPlaceholder: "Describe the property: condition, view, features, what is nearby…",
    licence: "Advertising licence no.",
    licencePlaceholder: "Required in this market",
    t4: "Photos and video",
    t4hint: "The first photo is the cover. Drag to reorder.",
    video: "Video tour link (optional)",
    t5: "Price",
    t5hint: "The price appears in search results and on the listing page.",
    price: "Price",
    advisorPriceHint: "Once published, the advisor can compare your price with similar listings in the same area.",
    t6: "Review",
    t6hint: "Check everything before publishing. You can edit any step.",
    edit: "Edit",
    sum1: "Type and purpose",
    sum2: "Location",
    sum3: "Details",
    sum4: "Photos and video",
    sum5: "Price",
    minPhotos: "A property cannot be published without photographs. Add at least {n}.",
    minPhotosShort: "Add at least {n}",
    photosN: "{n} photos",
    hasVideo: "video",
    terms: "I agree to the listing terms: the details are accurate, and the photos are of this property.",
    termsLink: "Terms",
    missing: "Please complete the required fields ({n}).",
    doneTitle: "Your listing has been sent for review",
    doneBody: "Review is usually quick, and we will tell you when it goes live.",
    dashboard: "Dashboard",
    another: "Another listing",
    limitTitle: "You have reached your plan's listing limit",
    limitBody: "Your plan allows a limited number of active listings. You can keep this one as a draft or upgrade to publish now.",
    upgrade: "Upgrade plan",
    failed: "The listing could not be published. Try again.",
    signInTitle: "Sign in to publish your listing",
    signInBody: "A listing belongs to your account, with its messages and view counts.",
    signIn: "Log in",
  },
  /* Arabic-only screen in the package; this English is ours. */
  plans: {
    title: "Plans for listers",
    hint: "Pick what fits the number of properties you have. Names, prices and limits are managed per market from the admin console.",
    perMonth: "/ month",
    activeListings: "Active listings",
    aiCredits: "Advisor credits",
    current: "Your current plan",
    currentCta: "Current plan",
    startFree: "Start free",
    upgrade: "Upgrade",
    choose: "Choose plan",
    compare: "Compare plans",
    unlimited: "Unlimited",
    free: "Free",
    error: "Plans could not be loaded.",
    retry: "Try again",
    empty: "No plans are available right now.",
    faq: "Frequently asked",
  },
  /* Arabic-only screen in the package; this English is ours. */
  account: {
    nav: "My account",
    dashboard: "Dashboard",
    listings: "My listings",
    messages: "Messages",
    plan: "My plan",
    saved: "Saved",
    profile: "Account",
    welcome: "Hello, {name}",
    welcomeHint: "Your listings, your messages and your plan.",
    statActive: "Active listings",
    statUnread: "New messages",
    statThreads: "Conversations",
    statViews: "Total views",
    statAi: "Advisor credits",
    newListing: "New listing",
    filterAll: "All",
    filterLive: "Published",
    filterReview: "In review",
    filterPaused: "Paused",
    filterRejected: "Rejected",
    colListing: "Listing",
    colPrice: "Price",
    colStatus: "Status",
    colViews: "Views",
    colMessages: "Messages",
    statusLive: "Published",
    statusReview: "In review",
    statusPaused: "Paused",
    statusRejected: "Rejected",
    edit: "Edit",
    pause: "Pause",
    resume: "Republish",
    view: "View",
    recentMessages: "Recent messages",
    allMessages: "All messages →",
    noMessages: "No messages yet.",
    myPlan: "My plan",
    usageListings: "Active listings",
    usageAi: "Advisor credits",
    unlimited: "Unlimited",
    emptyTitle: "You have no listings yet",
    emptyBody: "Publish your first in minutes and hear from interested buyers directly.",
    error: "The dashboard could not be loaded.",
    localDraft: "You have a draft saved on this device",
    continueDraft: "Continue the draft",
    unreadNote: "“New messages” is counted on this device, from the last time you opened Messages.",
  },
  /* Arabic-only screen in the package; this English is ours. */
  chat: {
    title: "Messages",
    all: "All",
    search: "Search conversations…",
    conversation: "Conversation",
    deletedUser: "مستخدم محذوف",
    viewListing: "View listing",
    publicProfile: "Public profile",
    placeholder: "Write your message…",
    message: "Message",
    send: "Send",
    hint: "Enter to send · Shift+Enter for a new line",
    failed: "The message could not be sent. Try again.",
    empty: "No conversations yet",
    emptyBody: "When you contact a lister, the conversation appears here.",
    error: "Conversations could not be loaded.",
    open: "Messages",
    close: "Close",
    back: "Back",
    today: "Today",
    yesterday: "Yesterday",
    noneSelected: "Pick a conversation from the list.",
    signIn: "Sign in to read your messages.",
  },
  /* Arabic-only screen in the package; this English is ours. */
  profile: {
    memberSince: "Member since",
    viewPublic: "View my public profile →",
    personal: "Personal details",
    name: "Name",
    phone: "Phone",
    email: "Email",
    language: "Language",
    verified: "Verified",
    notVerified: "Not verified",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    saveFailed: "Could not save. Try again.",
    emailFixed: "Your email is the account identifier and is not changed here.",
    security: "Security",
    password: "Password",
    changePassword: "Change password",
    plan: "My plan",
    upgrade: "Upgrade plan",
    usageListings: "Active listings",
    usageAi: "Advisor credits",
    renewsAt: "Renews on",
    saved: "Saved properties",
    unsave: "Remove from saved",
    noSaved: "You have not saved a property yet.",
    signOut: "Log out",
    dangerTitle: "Delete account",
    dangerBody: "Your account is deactivated at once and your listings are hidden. Your personal data is deleted for good after 30 days. You can undo it any time in that period by signing in.",
    dangerKeeps: "Messages stay with the other party, where your name shows as «مستخدم محذوف».",
    dangerCta: "Delete account",
    dangerConfirmTitle: "Confirm account deletion",
    dangerConfirmBody: "Type DELETE to confirm.",
    dangerConfirmWord: "DELETE",
    dangerConfirmCta: "Confirm deletion",
    dangerFailed: "The request could not be completed. Try again.",
    pendingTitle: "Your account is scheduled for deletion",
    pendingBody: "Your data is deleted for good 30 days after {when}. Sign in before then to undo it.",
    pendingUnpublished: "{n} of your listings were hidden.",

    listerListings: "{name}'s listings",
    contact: "Contact",
    all: "All",
    forSale: "For sale",
    forRent: "To rent",
    noListings: "No published listings right now.",
    notFound: "This profile is not available.",
  },
  /* The console is Arabic-first like the rest of the site; this English is ours. */
  cms: {
    imageChoose: "Choose an image",
    imageReplace: "Replace image",
    imageRemove: "Remove",
    imagePreparing: "Preparing sizes\u2026",
    imageUploading: "Uploading\u2026 {done}/{total}",
    imageSizes: "Uploaded sizes: {widths}px \u2014 the browser picks the one that fits the screen.",
    imageTooLarge: "That image is larger than {mb}MB. Choose a smaller one.",
    imageWrongType: "Accepted formats: JPEG, PNG or WebP.",
    imageUnreadable: "That image could not be read.",
    imageUploadFailed: "The upload failed. {detail}",
    areaImages: "Featured area images",
    areaImagesHint: "One picture per area. With none, the tile keeps the design's navy.",
    areaImagesNeedIds: "Areas that come from text a lister typed have no stable id, so no picture can be attached.",
    /* Property types — Admin owns the list the whole site reads. */
    types: "Property types",
    typesHint: "Drag to reorder. This order is the site's order everywhere: home tiles, search filters and the listing wizard.",
    typeAdd: "Add a type",
    areasOrder: "Area order and visibility",
    areasOrderHint: "Drag to reorder. This is the home page's order. Hiding an area only keeps it off the home page \u2014 its results page still works.",
    areaVisible: "Shown on the home page",
    areasPhotosFirst: "Areas with photos first",
    areasPhotosFirstHint: "On: areas with a photograph come first in your order, then the ones without, also in your order.",
    areasPhotosOnly: "Only show areas that have photos",
    areasRequireListings: "Only show areas that have listings",
    areasRequireListingsHint: "Off by default: an area with a photograph appears on the home page even with nothing listed in it yet — and its own page says so honestly and invites the first listing.",
    areasCount: "Area tiles on the home page",
    areasCountHint: "If more areas are visible than this, the last tile becomes «All areas».",
    imageTooNarrow: "This image is small and may look soft on large screens \u2014 1600px or wider is better.",
    minPhotosSetting: "Minimum photos to publish",
    minPhotosSettingHint: "No property in this market is published with fewer photographs than this. Minimum 1. Applies to the listing wizard and to admin approval. Existing listings are not changed.",
    homeTypeCount: "Type tiles on the home page",
    homeTypeCountHint: "How many tiles «Browse by type» shows. If more types exist, the last tile becomes «All types». Disabling a type removes it site-wide; this only limits the home row.",
    typeEdit: "Edit",
    typeCancel: "Cancel",
    typeNameAr: "Arabic name",
    typeNameEn: "English name",
    typeIcon: "Icon",
    typeCategory: "Category",
    typeFields: "Applicable fields",
    typeFieldsHint: "The wizard and the filters show only the fields you tick. Land, for instance, has no rooms or bathrooms.",
    typeEnabled: "Offered in this market",
    typeArchived: "Archived",
    typeArchive: "Archive",
    typeRestore: "Restore",
    typeDelete: "Delete",
    typeMoveUp: "Move up",
    typeMoveDown: "Move down",
    typeInUse: "listings use this type",
    typeCannotDelete: "A type with listings behind it cannot be deleted. Archiving hides it from the site and from new listings, and leaves the existing listings exactly as they are.",
    typeArchiveExplain: "Hidden from the site and from new listings. The listings that use it are untouched and still visible.",
    typeDeleteConfirm: "Delete this type permanently? No listings use it.",
    typeCatResidential: "Residential",
    typeCatCommercial: "Commercial",
    typeCatLand: "Land",
    typeHomeMode: "\u201cBrowse by type\u201d on the home page",
    typeHomeModeAll: "Every enabled type",
    typeHomeModeWith: "Only types that have listings",
    typeHomeModeHint: "An empty type opens the results page with its honest empty state.",
    fieldArea: "Area",
    fieldBeds: "Rooms",
    fieldBaths: "Bathrooms",
    fieldKitchens: "Kitchens",
    fieldFurnished: "Furnished",
    fieldFloor: "Floor",
    fieldLandArea: "Land area",
    group: "Site settings",
    content: "Content",
    home: "Home page",
    navigation: "Navigation",
    general: "General",
    pages: "Content pages",
    markets: "Markets",
    market: "Market",
    save: "Save",
    saving: "Saving…",
    saved: "Changes saved",
    allSaved: "All changes saved",
    unsaved: "Unsaved changes",
    saveFailed: "Could not save.",
    needsWorker: "This screen needs proxy v3.8.6, which has not been deployed. See §1 of docs/REBUILD-1-REPORT.md.",
    loadFailed: "Settings could not be loaded.",
    hero: "Hero",
    heroTitle: "Headline",
    heroSubtitle: "Subheading",
    heroImage: "Background image",
    heroImageHint: "A 16:9 image URL. Atmosphere only — never shown as a listing.",
    sections: "Page sections",
    sectionsHint: "Order the sections. A hidden section is not shown to visitors.",
    visible: "Visible",
    moveUp: "Up",
    moveDown: "Down",
    featured: "Featured properties",
    featuredCount: "How many (1–8)",
    tagPriority: "Tag priority",
    listerBand: "Lister band",
    bandTitle: "Heading",
    bandBody: "Body",
    bandCta: "Button label",
    navItems: "Navigation items",
    navHint: "The label appears in the header. The destination is fixed in code — a route is not content.",
    navCta: "Header button",
    label: "Label",
    destination: "Destination",
    noBackend: "Not available yet",
    contact: "Contact details",
    email: "Email",
    phone: "Phone",
    whatsapp: "WhatsApp",
    social: "Social",
    countThreshold: "Listing-count threshold",
    countThresholdHint: "Leave it empty and no count is shown anywhere.",
    pageSlug: "Slug",
    pageLocale: "Language",
    pageTitle: "Title",
    pageBody: "Body (Markdown)",
    pageMeta: "Search description",
    published: "Published",
    newPage: "New page",
    noPages: "No pages yet.",
    marketName: "Name",
    marketSlug: "Slug",
    visibility: "Visible to visitors",
    visibilityHint: "«Visible» offers the market in the switcher and footer. «Coming soon» offers it with a label. «Hidden» removes it entirely and sends its URLs to a visible market — none of its other data changes.",
    visActive: "Visible",
    visComingSoon: "Coming soon",
    visHidden: "Hidden",
    visLastOne: "This is the last visible market — at least one must stay visible to visitors.",
    marketActive: "Active",
    currency: "Currency",
    currencySymbol: "Symbol",
    currencyFormat: "Price format",
    currencyDisplay: "Currency display",
    currencyFormatHint: "Use {n} where the number goes — for example {n} ج.م or EGP {n}",
    currencyNeedsN: "The format must contain {n}, where the number goes.",
    timezone: "Timezone",
    phoneCode: "Phone code",
    tax: "Tax %",
    gateway: "Payment gateway",
    requiresLicence: "Requires an advertising licence",
    featureProjects: "Projects",
    featureAdvisor: "AI advisor",
    texts: "Site texts",
    textsHint: "Every visible string on the site. Empty means the designed wording.",
    searchTexts: "Search the texts…",
    onlyEdited: "Edited only",
    value: "Text",
    original: "Default",
    resetToDefault: "Restore the default",
    maxChars: "Limit {n} characters",
    tooLong: "{n} characters over the limit.",
    noMarkup: "Plain text only — no HTML.",
    editedBy: "Last edited: {who} · {when}",
    notEdited: "not edited",
    nothingFound: "No matching texts.",
    preview: "Preview",
    previewAfterSave: "The preview shows the site as it is now — it updates after you save.",
    previewMobile: "Phone",
    previewDesktop: "Desktop",
    textsCount: "{n} strings",
    projectsDisabled: "There is no projects backend — the proxy refuses to enable it.",
  },
  /* L1a — see the Arabic block above. */
  lifecycle: {
    crumb: "Markets",
    settingsTitle: "Listings",
    settingsHint: "Every number here belongs to one market and is edited here. There is no fallback value: a missing row shows as an error and is never guessed.",
    notSeeded: "These settings have not been set up for this market yet (migration 015 must be applied).",
    badValue: "That value does not fit this setting.",
    unlimited: "No limit",
    groupReview: "Review",
    groupCycle: "The listing's life",
    groupLimits: "Limits and duplicates",
    reviewMode: "Review mode",
    reviewModeHint: "“All listings” reviews every new listing. The other two modes are stored but not enforced yet.",
    modeAll: "All listings",
    modeFirstOnly: "First listing only",
    modeTrustedAuto: "Automatic for verified",
    availabilityCheckDays: "Ask “still available?” after (days)",
    autoPauseSilentDays: "Pause after silence (days)",
    expiredGraceDays: "Renewal window after expiry (days)",
    archiveRestoreDays: "Archive restore window (days)",
    freeDurationDays: "Free listing duration (days)",
    freeCeiling: "Active-listing ceiling — unverified account",
    verifiedCeiling: "Active-listing ceiling — verified broker",
    verifiedCeilingHint: "Empty = no ceiling.",
    duplicateWindowDays: "Duplicate-detection window (days)",
    duplicateMatchFields: "Fields compared",
    fieldPhone: "Phone number",
    fieldDistrict: "District",
    fieldPriceBand: "Price band",
    priceBandPercent: "Price band ± (%)",
    refreshMinHours: "Minimum gap between refreshes (hours)",
    reasonsCrumb: "Markets",
    reasonsTitle: "Rejection reasons",
    reasonsHint: "These are the reasons offered when a listing is rejected. Archiving hides a reason from the list without touching the listings that used it.",
    reasonKey: "Key",
    reasonKeyHelp: "Lowercase letters, digits and underscores. It cannot change once added.",
    reasonLabelAr: "Arabic text",
    reasonLabelEn: "English text",
    reasonOrder: "Order",
    reasonAdd: "Add a reason",
    reasonArchive: "Archive",
    reasonActivate: "Activate",
    reasonArchived: "Archived",
    reasonsEmpty: "This market has no rejection reasons yet.",
    reviewCrumb: "Listings",
    reviewTitle: "Review queue",
    reviewEmpty: "Nothing is waiting for review.",
    reviewApprove: "Approve",
    reviewReject: "Reject",
    reviewPickReason: "Choose a reason",
    reviewNote: "Note to the lister (optional)",
    reviewConfirmReject: "Confirm rejection",
    reviewCancel: "Cancel",
    reviewDuplicate: "Suspected duplicate",
    reviewMatches: "This looks like:",
    reviewApproved: "Approved ✓",
    reviewRejected: "Rejected ✓",
    reviewNeedsReason: "Choose a reason first.",
    reviewFailed: "That action could not be done.",
    reviewChanged: "The listing changed; reload the queue.",
    cronTitle: "Last run of the scheduled jobs",
    cronStarted: "Started",
    cronExpired: "expired",
    cronArchived: "archived",
    cronNudged: "asked “still available?”",
    cronPaused: "paused automatically",
    confirmedToday: "Confirmed today",
    confirmedAgo: "Confirmed {n} days ago",
    ceilingTitle: "You have reached the listing limit",
    ceilingBody: "You can keep {n} active or in-review listings at once. Pause or archive an old one to post a new one, or verify your account to raise the limit.",
    ceilingVerify: "Verify your account for free",
  },
  results: {
    filters: "Filters",
    purpose: "Purpose",
    buy: "Buy",
    rent: "Rent",
    cityAndArea: "City and area",
    city: "City",
    anyCity: "All cities",
    area: "Area",
    anyArea: "All areas",
    propertyType: "Property type",
    price: "Price",
    from: "From",
    to: "To",
    rooms: "Rooms",
    any: "Any",
    videoOnly: "Video tour only",
    clearFilters: "Clear filters",
    sort: "Sort",
    sortNewest: "Newest",
    sortPriceAsc: "Price: low to high",
    sortPriceDesc: "Price: high to low",
    sortArea: "Area",
    view: "View",
    viewList: "List",
    viewMap: "Map",
    home: "Home",
    titleAll: "Properties",
    titleFor: "{types} {purpose}",
    titleIn: "{what} in {where}",
    forSale: "for sale",
    forRent: "to rent",
    emptyTitle: "No matching results",
    emptyBody: "Try widening the area or removing some filters.",
    emptyAreaTitle: "No listings in {area} yet",
    emptyAreaBody: "Be the first to list a property here.",
    emptyAreaCta: "List your property",
    error: "The results could not be loaded. Check your connection and try again.",
    loading: "Loading results",
    pages: "Pages",
    prev: "Previous",
    next: "Next",
    page: "Page {n}",
    apply: "Apply",
    close: "Close",
  },
  myListings: {
    eyebrow: "My listings",
    title: "My listings",
    search: "Search your listings…",
    listProperty: "List your property",
    tabAll: "All",
    tabActive: "Active",
    tabPending: "In review",
    tabRejected: "Rejected",
    tabPaused: "Paused",
    tabExpired: "Expired",
    tabSold: "Sold",
    tabArchived: "Archived",
    tabDraft: "Draft",
    colListing: "Listing",
    colStatus: "Status",
    colViews: "Views · Saves",
    colConversations: "Conversations",
    colActions: "Actions",
    saves: "{n} saved",
    stActive: "Active",
    stPending: "In review",
    stRejected: "Rejected",
    stPaused: "Paused",
    stPausedByLimit: "Paused",
    stExpired: "Expired",
    stSold: "Sold",
    stRented: "Rented",
    stArchived: "Archived",
    stDraft: "Draft",
    daysLeft: "{n} days left",
    daysLeftOne: "1 day left",
    endsToday: "Ends today",
    rejectedWhen: "Rejected {when}",
    rejectedNoWhen: "Rejected",
    pendingSince: "In review since {when}",
    pausedSince: "Paused since {when}",
    pausedUnconfirmed: "Paused — availability not confirmed",
    pausedByLimit: "Paused — over the limit",
    expiredOn: "Expired {when}",
    soldOn: "Sold {when}",
    rentedOn: "Rented {when}",
    archivedOn: "Archived {when}",
    draftMeaning: "Not submitted yet",
    actView: "View",
    actEdit: "Edit",
    actComplete: "Continue",
    hoursAgo: "{h} h",
    daysAgo: "{d} days",
    actRefresh: "Refresh",
    actPause: "Pause",
    actResume: "Resume",
    actRenew: "Renew",
    actSold: "Mark sold",
    actRented: "Mark rented",
    actShare: "Share",
    actArchive: "Archive",
    actRestore: "Restore",
    actConfirm: "Still available ✓",
    actResubmit: "Fix and resubmit",
    actMore: "More",
    actionsLegend: "Actions: view · edit · refresh · pause/resume · renew · sold/rented · share · archive — grey = not available in this status.",
    soldEyebrow: "Sold",
    rentedEyebrow: "Rented",
    soldTitle: "Congratulations! Has “{title}” sold?",
    rentedTitle: "Congratulations! Has “{title}” been rented?",
    soldBody: "We stop the messages and mark it “Sold” for buyers. Your views and stats stay in your account.",
    rentedBody: "We stop the messages and mark it “Rented” for seekers. Your views and stats stay in your account.",
    priceLabel: "Sold for how much?",
    priceLabelRent: "Rented for how much?",
    priceHint: "(optional — private, never published, improves price advice for you and the area)",
    cancel: "Cancel",
    soldConfirm: "Sold ✓",
    rentedConfirm: "Rented ✓",
    archiveTitle: "Archive “{title}”?",
    archiveBody: "It leaves search results and lowers your active count. You can bring it back from the “Archived” tab — it returns to review, with its views.",
    archiveConfirm: "Archive",
    shareTitle: "Share the listing",
    shareCopy: "Copy",
    shareCopied: "Copied",
    shareWhatsapp: "WhatsApp",
    shareFacebook: "Facebook",
    toastRefreshed: "Listing refreshed — it shows as if posted now",
    toastPaused: "Listing paused",
    toastResumed: "The listing is visible to seekers again",
    toastRenewed: "Renewed — {n} new days",
    toastArchived: "Archived",
    toastRestored: "Back in review — it publishes again once approved",
    toastSold: "Marked as sold",
    toastRented: "Marked as rented",
    toastConfirmed: "Thanks — availability confirmed",
    undo: "Undo",
    errStale: "The listing changed meanwhile — the list was refreshed.",
    errCeiling: "You have reached {ceiling} active listings. Verify your account for free to lift the limit.",
    errCeilingLink: "Verify your account for free",
    errTooSoon: "You can refresh again in {n} hours.",
    errNotIncluded: "Your plan does not include refreshes.",
    errUpgrade: "Upgrade your plan",
    errExpired: "The listing has expired — renew it first.",
    errRenewWindow: "The renewal window has passed — restore it from “Archived”.",
    errRestoreWindow: "The restore window has passed.",
    errPhotos: "The listing needs at least {need} photos before publishing (it has {have}).",
    errNotSetUp: "Settings are incomplete — contact support.",
    errGeneric: "That did not go through — try again.",
    emptyAllTitle: "You have not listed a property yet",
    emptyAllBody: "Your first listing appears here with its status and stats.",
    emptyTabTitle: "Nothing here",
    emptyTabBody: "No listings in this status right now.",
    emptySoldTitle: "No sold listings yet",
    emptySoldBody: "When you close a deal, press “Sold” on the listing — it moves here and stops the messages.",
    errorTitle: "Could not load your listings",
    retry: "Try again",
    loading: "Loading…",
  },
  dash: {
    eyebrow: "Dashboard",
    hello: "Hi {name} 👋",
    listProperty: "List your property",
    attnTitle: "Needs you now",
    attnCount: "{n} items",
    attnClear: "All clear",
    attnClearTitle: "Nothing urgent",
    attnClearBody: "Your listings are live. Last check {when}.",
    attnNotSetUp: "The attention list is not set up yet — contact support.",
    justNow: "moments ago",
    rejectedTitle: "Listing rejected: “{title}”",
    rejectedBody: "Reason: {reason}",
    rejectedNoReason: "Review the listing and edit it",
    resubmit: "Fix and resubmit",
    expiringTitle: "A listing expires in {n} days",
    expiringTitleOne: "A listing expires tomorrow",
    expiringTitleToday: "A listing expires today",
    expiringBody: "“{title}” — if it is still available, renew it in one tap, with its views and saves.",
    expiringDangerBody: "After expiry it leaves search and {saves} people who saved it see “Expired”. Renew in one tap.",
    renew: "Renew",
    renewNow: "Renew now",
    markSold: "Sold",
    markRented: "Rented",
    markOutcome: "Sold / rented",
    availTitle: "Still available? “{title}”",
    availBody: "{n} days without confirmation — confirm so the listing keeps its “Confirmed” badge",
    confirmAvailable: "Still available ✓",
    quotaTitle: "You have used {used} of {ceiling} listings",
    quotaBody: "One slot left — archive an old listing or verify your account for free to lift the limit.",
    quotaBodyMany: "{left} slots left — archive an old listing or verify your account for free to lift the limit.",
    archiveOld: "Archive old ones",
    verify: "Verify your account",
    planFree: "Free plan",
    planRenews: "Renews automatically on {date} · {price}",
    planManage: "Manage",
    planFreeLine: "No renewal · listing is free",
    usageActive: "Active listings",
    usageActiveNoLimit: "{n} active listings",
    usageAi: "AI advisor consultations",
    usageAiLeft: "{n} left",
    usageAiUnlimited: "Unlimited",
    usageAiNone: "Not included in Free",
    upgrade: "Upgrade your plan",
    upgradeFrom: "Upgrade — from {price}",
    comparePlans: "Compare plans",
    listingsTitle: "Your listings",
    allListings: "All listings →",
    perfTitle: "Your performance",
    statViews: "Total views",
    statViewsSub: "across {n} active listings",
    statSaves: "Saves",
    statSavesSub: "added your listings to favourites",
    statViewsZero: "Views start after publishing",
    statSavesZero: "Saves start after publishing",
    statMsgsZero: "Messages show up here",
    threadsTitle: "Latest conversations",
    threadsAll: "All →",
    threadsEmpty: "No conversations yet.",
    firstTitle: "List your first property",
    firstBody: "4 steps, under 5 minutes.",
    errorTitle: "Could not load the dashboard",
    errorBody: "Your listings are fine — only the display failed.",
    retry: "Try again",
  },
  wizard2: {
    pageTitle: "List your property",
    saveExit: "Save and exit",
    saveDraftExit: "Save draft and exit",
    back: "Back",
    nextTo: "Next: {name}",
    submit: "Send for review",
    resubmit: "Resend for review",
    stepOfM: "Step {n} of {total} — {name}",
    nextM: "Next: {name}",
    steps: [
      { name: "Property & location", sub: "Type, location, location privacy" },
      { name: "Details & price", sub: "Area, rooms, price and description" },
      { name: "Photos & video", sub: "At least {min} — the first is the cover" },
      { name: "Review & publish", sub: "Check the listing, then send it for review" },
    ],
    saved: "Saved automatically · {when}",
    saving: "Saving…",
    saveFailed: "Couldn't save — we'll retry",
    notSavedYet: "Saves automatically as you type",
    justNow: "just now",
    restored: "We restored your draft from",
    restoredTail: "— carry on where you left off.",
    startOver: "Start over",
    startOverTitle: "Start over?",
    startOverBody: "The current draft moves to the “Archived” tab in My listings, and you start an empty one.",
    startOverYes: "Start over",
    cancel: "Cancel",
    s1eyebrow: "Step 1 · Property & location",
    s1title: "What are you listing?",
    purpose: "Listing type",
    forSale: "For sale",
    forRent: "For rent",
    propType: "Property type",
    whereTitle: "Where is it?",
    whereSubManual: "Pick the city and district from the lists, and type the street address.",
    country: "Country",
    city: "City",
    district: "District",
    pickCity: "Choose a city…",
    pickDistrict: "Choose a district…",
    errPickDistrict: "Choose a district",
    errPickCity: "Choose a city",
    errPickType: "Choose a property type",
    address: "Street address",
    addressNote: "(shown to searchers according to location privacy)",
    addressPlaceholder: "Street, building number, landmark",
    privacy: "Location privacy:",
    precise: "Exact location",
    approx: "Approximate area only",
    approxHint: "Searchers see an approximate circle with no directions link — the full address is sent when they get in touch.",
    preciseHint: "The exact pin shows, with a directions link.",
    locateToContinue: "Set the property's location to continue",
    s2eyebrow: "Step 2 · Details & price",
    s2title: "Details & price",
    titleLabel: "Listing title",
    titleNote: "(required · up to {max} characters)",
    titlePlaceholder: "e.g. 3-bedroom flat with a private garden, First District",
    errTitle: "Write a title for the listing",
    areaLabel: "Area",
    landAreaLabel: "Land area",
    sqm: "m²",
    beds: "Bedrooms",
    baths: "Bathrooms",
    kitchens: "Kitchens",
    furnished: "Furnished?",
    yes: "Yes",
    no: "No",
    finishing: "Finishing",
    finishingPick: "Choose…",
    finishingLevels: { core_shell: "Core & shell", semi: "Semi-finished", lux: "Lux", super_lux: "Super lux", ultra_lux: "Ultra super lux" },
    priceTitle: "Price",
    priceLabel: "Asking price",
    required: "(required)",
    perMonth: "/ month",
    errPrice: "Enter the price ({currency})",
    errArea: "Enter the area",
    options: "Options",
    negotiable: "Negotiable",
    installments: "Instalments available",
    deposit: "One month's deposit",
    descTitle: "Description",
    descSub: "At least {min} characters — mention nearby services and what makes it special.",
    descPlaceholder: "Describe the property, what's nearby and anything special…",
    descCounter: "{n} / at least {min} characters",
    previewEyebrow: "Live preview",
    previewSub: "As searchers will see it",
    previewNoPhoto: "Your cover photo will show here",
    previewPrice: "Price",
    previewTitle: "Listing title",
    tipsEyebrow: "Tips",
    tips1: ["“Approximate area” protects your address and is enough for a first look.", "An exact pin builds trust for villas and commercial property."],
    tips2: ["A clear title names the type, the rooms and the area.", "A real price brings more messages than a misleadingly low one."],
    signInTitle: "Sign in to list your property",
    signInBody: "The listing is kept in your account as a draft until you send it for review.",
    signIn: "Sign in",
    loading: "Loading your draft…",
    loadFailed: "Couldn't load the draft",
    retry: "Try again",
    limitsNotSetUp: "Listing limits are not set up for this market ({key}). Tell the admins.",
    notYourDraft: "That draft doesn't exist or isn't in your account.",
    s3eyebrow: "Step 3 · Photos & video",
    s3title: "Photos & video",
    s3sub: "Drag to reorder — the first one is the cover.",
    dropTitle: "Drop photos here or click to choose",
    dropSub: "At least {min} · JPG, PNG or WEBP · up to {mb} MB · the first becomes the cover",
    choosePhotos: "Choose photos",
    addPhoto: "Add a photo",
    cover: "COVER",
    coverShort: "Cover",
    ruleMin: "At least {min} ({n}/{k})",
    photoCount: { one: "1 photo", two: "2 photos", few: "{n} photos", many: "{n} photos" },
    ruleMax: "Up to {max} photos",
    ruleTypes: "JPG · PNG · WEBP",
    ruleMb: "Up to {mb} MB per photo",
    rulePx: "At least {px} px wide",
    maxReached: "You've reached the limit: {max} photos. Remove one to add another.",
    failNetwork: "Upload failed",
    failTooLarge: "Larger than {mb} MB",
    failBadType: "Unsupported type",
    failTooSmall: "Smaller than {px} px",
    retryUpload: "Try again",
    pickAnother: "Choose another",
    removePhoto: "Remove photo {n}",
    moveEarlier: "Move photo {n} earlier",
    moveLater: "Move photo {n} later",
    makeCover: "Make it the cover",
    photosNeeded: "Add at least {min} to continue",
    photosUploading: "Wait for the photos to finish uploading",
    videoTitle: "Video tour",
    optional: "(optional)",
    videoLabel: "YouTube link",
    videoHint: "Paste a YouTube video link — it plays inside the listing.",
    videoBad: "That isn't a YouTube video link",
    videoOk: "✓ The video will show in the listing",
    videoRemove: "Remove",
    tips3: ["Shoot in daylight with the curtains open.", "Make the cover the living room or the front — not the bathroom."],
    s4eyebrow: "Step 4 · Review & submit",
    s4title: "Check your listing before you send it",
    s4sub: "Once sent, our team reviews it within {n} hours and tells you when it's live.",
    rowPurpose: "Listing type",
    rowLocation: "Location",
    rowTitle: "Title",
    rowDetails: "Details",
    rowPrice: "Price",
    rowDescription: "Description",
    rowMedia: "Photos & video",
    edit: "Edit",
    roomsN: "{n} rooms",
    bathsN: "{n} baths",
    kitchensN: "{n} kitchens",
    furnishedYes: "Furnished",
    furnishedNo: "Unfurnished",
    withVideo: "video",
    none: "—",
    incomplete: "The “{name}” step isn't finished — complete it before sending.",
    goFix: "Go to the step",
    terms: "I confirm I own the property or am authorised to list it, and the details are correct. I agree to the",
    termsLink: "listing terms",
    termsNeeded: "Agree to the listing terms to continue",
    disclosure: "Your name {name} shows on the listing. Your phone number is never published — contact goes through SouqAkar messages.",
    disclosureVerified: "Your name {name} and the {badge} badge show on the listing. Your phone number is never published — contact goes through SouqAkar messages.",
    verifiedBadge: "Verified agent ✓",
    submitting: "Sending…",
    submitError: "Couldn't send the listing — nothing is lost, your draft is saved. Check your connection and try again.",
    quotaReached: "You've reached your plan's listing limit. We'll keep this as a draft — upgrade to publish it.",
    upgrade: "Upgrade",
    photosRefused: "The listing needs at least {min} photos before it can be sent — it has {have}.",
    stale: "The listing's status changed elsewhere. Refresh the page.",
    reload: "Refresh",
    rejectedBanner: "Previous rejection reason: {reason} — make sure it's fixed before resending.",
    rejectedDefault: "The listing didn't meet the publishing rules. Check the photos, the price and the description.",
    tips4: ["Read the title and price as a searcher would.", "You can edit the listing while it's under review."],
    successEyebrow: "Submitted",
    successTitle: "We've got your listing — it's under review",
    resubmittedEyebrow: "Resubmitted",
    resubmittedTitle: "Your listing is back in review",
    successBody: "Our team usually reviews it within {n} hours. We'll tell you as soon as it's live — in SouqAkar messages.",
    pending: "Under review",
    viewInMine: "View in My listings",
    listAnother: "+ List another property",
    dashboard: "Dashboard",
    editWhilePending: "You can edit it while it's under review without resending.",
    meanwhileEyebrow: "Meanwhile",
    meanwhile: ["Share the listing with people you know once it's live, from «Share».", "Messages arrive in «Messages» — a quick reply makes a difference."],
    comingNext: "This step arrives in the next version of the wizard.",
  },
};

export const v3Dictionaries: Record<Locale, V3Strings> = { ar, en };

/** Fill {name} placeholders. A missing key is left visible, never blanked. */
/** «صورة واحدة / صورتان / ٣ صور / ١١ صورة» — Arabic agreement for a count read from a row. */
export function photoWord(W: { photoCount: { one: string; two: string; few: string; many: string } }, n: number, digits: string): string {
  const f = n === 1 ? W.photoCount.one : n === 2 ? W.photoCount.two : n % 100 >= 3 && n % 100 <= 10 ? W.photoCount.few : W.photoCount.many;
  return f.replace("{n}", digits);
}

export function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, k: string) => vars[k] ?? whole);
}
